
import { type ReactNode, forwardRef, memo, useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import { OrderType, ParamType } from "../../types/grid.ts";
import type { InputParam, GridAttributes } from "../../types/grid.ts";
import type { ColumnDescription, WebAdminPage, WebAdminPageData } from "../../types/session.ts";
import { useSuperAdminPanel } from "../../tools/superAdmin/SuperAdminPanelProvider.tsx";
import { useGridData } from "../../hooks/useGridData.ts";
import { useFilterUrlSync } from "../../hooks/useFilterUrlSync.ts";
import { useAuth } from "../../auth/authContext.tsx";
import { useToast } from "../../hooks/useToast.ts";
import { FilterBar, INLINE_FILTER_TYPES } from "./filterBar.tsx";
import { Pagination } from "./pagination.tsx";
import { ColumnConfig } from "./columnConfig.tsx";
import { renderCellValue, resolveCellTooltip } from "./cellRenderer.tsx";
import type { ButtonClickInfo } from "./cellRenderer.tsx";
import { getGridOverrideBuilder, getCellDecorator, getCellConfirmBuilder, getCellLockedBuilder } from "./gridOverrides.tsx";
import type { CellCommitResult, GridOverride } from "./gridOverrides.tsx";
import { useAlertDialog } from "../../hooks/useAlertDialog.tsx";
import { resolveHyperlinkAction } from "./hyperlinkDispatcher.ts";
import { PagePopup } from "./pagePopup.tsx";
import { updateGridCell, deleteGridCell } from "../../api/endpoints.ts";
import { AdvancedFilterBar, getAdvancedColumnKeys } from "./advancedFilters/advancedFilterBar.tsx";
import { FilterSiblingProvider, createFilterSiblingStore } from "./advancedFilters/filterSiblings.ts";
import { getFilter } from "./advancedFilters/registry.ts";
import "./advancedFilters/registerAll.ts";
import { ExportDialog } from "./exportDialog.tsx";
import { ScheduleExportDialog } from "./scheduleExportDialog.tsx";
import { ShareDialog } from "./shareDialog.tsx";
import { DropdownMenu, DropdownMenuItem, DropdownMenuSeparator } from "../ui/dropdownMenu.tsx";
import { CloseButton } from "../ui/closeButton.tsx";
import { FilterChipStrip } from "./filterChips.tsx";
import { useFilterBarMode } from "../../hooks/useFilterBarMode.ts";
import { exposeStateInUrl } from "../../hooks/useFilterUrlSync.ts";
import { SavedFiltersPanel, loadSavedFilters, resolveRelativeDates } from "./savedFiltersPanel.tsx";
import type { SavedFilter } from "./savedFiltersPanel.tsx";
import { Modal, ModalHeader, ModalBody, ModalFooter } from "../ui/modal.tsx";
import { StatusIcon } from "../ui/statusIcon.tsx";
import { AssistantAvatar } from "../ui/assistantAvatar.tsx";
import { useAssistant } from "../../config/assistantProfile.ts";
import { EmptyState } from "../ui/emptyState.tsx";
import { Alert } from "../ui/alert.tsx";
import { incompleteGridMessage } from "../../util/incompleteData.ts";
import { DataChart, buildChartExplainPrompt } from "./dataChart.tsx";
import { RowContextMenu } from "./rowContextMenu.tsx";
import { parseChartConfig } from "../../util/chartUtils.ts";
import { gridRequestFilters } from "../../util/mergeGridFilters.ts";
import styles from "./dataGrid.module.scss";

export interface DataGridHandle {
    refresh: () => void;
    search: () => void;
    /** Commit a single cell's value using the grid's own pivot-aware save path
     *  (same override hooks, key-column and pivot handling as inline cell
     *  editing) — for callers that edit cells from outside the grid, e.g. a
     *  row-edit modal. Returns whether the save succeeded. */
    commitCellValue: (rowIdx: number, col: string, value: string) => Promise<boolean>;
}

interface DataGridProps {
    name: string;
    /** Namespace for persisted filter state (sessionStorage/URL hash key
     *  `f.<persistKey>`). Defaults to `name`. Set this when several grids share
     *  the same server `name` (e.g. one grid mounted per tab) so each keeps its
     *  own filter state instead of colliding on `f.<name>`. */
    persistKey?: string;
    staticFilters?: InputParam[];
    hiddenFilters?: string[];
    hiddenColumns?: string[];
    /** Caller can pass partial column descriptors — only ColumnName is
     *  required; missing fields are filled with sensible defaults when
     *  merged into the info map. */
    extraColumnInfo?: (Partial<ColumnDescription> & Pick<ColumnDescription, "ColumnName">)[];
    onRowSelect?: (row: unknown[], colIndex: Record<string, number>, rowIdx: number, clickedColumn?: string) => void;
    onButtonClick?: (info: ButtonClickInfo, row: unknown[], colIndex: Record<string, number>, keyColumns: string[]) => void;
    editable?: boolean;
    onCellEdited?: () => void;
    onDataLoaded?: (columns: string[], rows: Record<string, unknown>[]) => void;
    autoSearch?: boolean;
    /** Hide toolbar, filters, and pagination — for embedded/inline grids */
    compact?: boolean;
    toolbarExtra?: React.ReactNode;
    hideToolbar?: boolean;
    chartOnly?: boolean;
    chartConfig?: import("../../types/chart.ts").ChartSettings;
    chartShowLabels?: boolean;
    /** Vertical time-marker annotations for line/area charts (e.g. door-open events). */
    chartAnnotations?: import("../chart/charts.tsx").LineAnnotation[];
    /** Emitted once the chart's time-axis bounds and series identities are known. */
    onChartMeta?: (meta: import("./dataChart.tsx").ChartMeta) => void;
    highlightColumn?: string;
    highlightValue?: string;
    /** Per-grid cell rendering / commit overrides. Merged with the central registry; prop wins on conflict. */
    gridOverride?: GridOverride;
    /** Always show the delete column, whatever the page's ClientOptions say
     *  (WebAdmin2 pages that forced generalDeleteButton = true). */
    deleteButton?: boolean;
}

const OPERATION_TYPES = new Set(["VIEW", "FN", "SP", "EXEC", "IMP"]);
const EDITABLE_TEXT_TYPES = new Set([
    "TEXTBOX", "TEXTBOXVERIFIED", "TEXTBOXPRICE", "TEXTBLOCKPRICE", "TEXTBOXEMPTY",
    "MAILADDRESS", "TEXTAREA",
]);
const TEXTAREA_TYPES = new Set(["TEXTBOXVERIFIED", "TEXTAREA"]);
const CONFIRM_CHECKBOX_TYPES = new Set(["CHECKBOXCONFIRM", "DATELOCK"]);
const ALL_EDITABLE_TYPES = new Set([...EDITABLE_TEXT_TYPES, ...CONFIRM_CHECKBOX_TYPES, "VALUEDCOMBOBOX", "CHECKBOX"]);
const ISO3_LANGUAGE_CODE_RE = /^[A-Z]{3}$/i;

// Synthetic action columns appended to the header when the SaveButton /
// DeleteButton ClientOptions flags are set (WebAdmin2 parity — these columns
// are NOT returned by the server; the grid injects them). The DisplayType
// drives the per-row button renderer in the cell switch below.
const GENERAL_SAVE_COL = "component_generalsave";
const GENERAL_DELETE_COL = "component_generaldelete";

// Per-grid persisted custom column selection (Spec C §6.3). Stored alongside a
// signature of the report's default visible set so a changed report definition
// invalidates the stale selection instead of silently mis-applying it.
interface StoredColumnSelection { columns: string[]; def: string }
const columnSelectionKey = (name: string) => `webadmincc-columns-${name.toUpperCase()}`;
function loadColumnSelection(name: string): StoredColumnSelection | null {
    try {
        const raw = localStorage.getItem(columnSelectionKey(name));
        if (!raw) return null;
        const parsed = JSON.parse(raw) as StoredColumnSelection;
        if (!Array.isArray(parsed.columns) || parsed.columns.length === 0 || typeof parsed.def !== "string") return null;
        return parsed;
    } catch { return null; }
}
function storeColumnSelection(name: string, columns: string[], def: string): void {
    try { localStorage.setItem(columnSelectionKey(name), JSON.stringify({ columns, def })); } catch { /* quota */ }
}
function clearColumnSelection(name: string): void {
    try { localStorage.removeItem(columnSelectionKey(name)); } catch { /* noop */ }
}

function resolveLanguageNameFromIso3(code: string): string {
    const normalized = code.trim().toLowerCase();
    if (!normalized) return code;
    try {
        const dn = new Intl.DisplayNames(["en"], { type: "language" });
        const name = dn.of(normalized);
        if (name && name.toLowerCase() !== normalized) {
            return name;
        }
    } catch {
        // Intl.DisplayNames may be unavailable in some runtimes.
    }
    return code.toUpperCase();
}
function toBoolCheck(value: unknown): boolean {
    if (value == null) return false;
    const s = String(value).toLowerCase();
    return s !== "0" && s !== "false" && s !== "no" && s !== "";
}

/** Parse a page/pivot ColumnDescJson blob into a lowercase-name-keyed map of
 *  sparse column-description overrides. Each value carries the original-case
 *  ColumnName plus whichever ColumnDescription fields were set. Malformed JSON
 *  yields an empty map (the column falls back to the global ColumnInfo). */
export function parseColumnDescJson(json: string | undefined): Record<string, Partial<ColumnDescription>> {
    if (!json) return {};
    try {
        const obj = JSON.parse(json) as unknown;
        if (!obj || typeof obj !== "object") return {};
        const out: Record<string, Partial<ColumnDescription>> = {};
        for (const [colName, fields] of Object.entries(obj as Record<string, unknown>)) {
            if (!fields || typeof fields !== "object") continue;
            out[colName.toLowerCase()] = { ColumnName: colName, ...(fields as Partial<ColumnDescription>) };
        }
        return out;
    } catch {
        return {};
    }
}

/** WebAdmin2 storage.ts parity: derive grid-behaviour booleans from the
 *  ClientOptions array. The server sends ClientOptions as string[] only —
 *  nothing else populates these flags, so the nosort / delete / save /
 *  refreshonupdate use cases all depend on this derivation. */
function applyClientOptionFlags(data: WebAdminPageData): WebAdminPageData {
    const opts = data.ClientOptions;
    if (!opts || opts.length === 0) return data;
    const has = (token: string) => opts.some((o) => o.toLowerCase() === token);
    return {
        ...data,
        NoSort: has("nosort"),
        DeleteButton: has("delete"),
        SaveButton: has("save"),
        RefreshOnUpdate: has("refreshonupdate"),
    };
}

/* ── Searchable ComboBox cell editor ────────────────────── */
interface ComboBoxEditorProps {
    options: { value: string; label: string }[];
    value: string;
    onCommit: (value: string) => void;
    onCancel: () => void;
}

function ComboBoxEditor({ options, value, onCommit, onCancel, anchorEl }: ComboBoxEditorProps & { anchorEl: HTMLElement | null }) {
    const [search, setSearch] = useState("");
    const [activeIdx, setActiveIdx] = useState(-1);
    const inputRef = useRef<HTMLInputElement>(null);
    const listRef = useRef<HTMLUListElement>(null);
    const [pos, setPos] = useState<{ top: number; left: number } | null>(null);

    useEffect(() => {
        if (!anchorEl) return;
        const rect = anchorEl.getBoundingClientRect();
        setPos({ top: rect.bottom, left: rect.left });
    }, [anchorEl]);

    useEffect(() => { inputRef.current?.focus(); }, [pos]);

    const filtered = useMemo(() => {
        const term = search.toLowerCase();
        return term ? options.filter(o => o.label.toLowerCase().includes(term)) : options;
    }, [options, search]);

    // Scroll active item into view
    useEffect(() => {
        if (activeIdx < 0 || !listRef.current) return;
        const el = listRef.current.children[activeIdx + 1] as HTMLElement | undefined;
        el?.scrollIntoView({ block: "nearest" });
    }, [activeIdx]);

    const handleKeyDown = (e: React.KeyboardEvent) => {
        if (e.key === "Escape") { onCancel(); return; }
        if (e.key === "ArrowDown") { e.preventDefault(); setActiveIdx(i => Math.min(i + 1, filtered.length - 1)); return; }
        if (e.key === "ArrowUp") { e.preventDefault(); setActiveIdx(i => Math.max(i - 1, -1)); return; }
        if (e.key === "Enter") {
            e.preventDefault();
            const activeItem = filtered[activeIdx];
            if (activeIdx >= 0 && activeItem) {
                onCommit(activeItem.value);
            } else if (activeIdx === -1 && search === "") {
                onCommit("");
            }
        }
    };

    if (!pos) return null;

    return createPortal(
        <div
            className={styles.comboEditorWrap}
            style={{ top: pos.top, left: pos.left }}
            onMouseDown={e => e.preventDefault()}
        >
            <input
                ref={inputRef}
                className={styles.comboEditorInput}
                type="text"
                placeholder="Search..."
                value={search}
                onChange={e => { setSearch(e.target.value); setActiveIdx(-1); }}
                onKeyDown={handleKeyDown}
                onBlur={onCancel}
            />
            <ul ref={listRef} className={styles.comboEditorList}>
                <li
                    className={`${styles.comboEditorItem} ${styles.comboEditorItemNone}${!value && activeIdx === -1 ? ` ${styles.comboEditorItemActive}` : ""}`}
                    onMouseDown={() => onCommit("")}
                >
                    None
                </li>
                {filtered.map((opt, i) => (
                    <li
                        key={opt.value}
                        className={`${styles.comboEditorItem}${opt.value === value ? ` ${styles.comboEditorItemActive}` : ""}${i === activeIdx ? ` ${styles.comboEditorItemActive}` : ""}`}
                        onMouseDown={() => onCommit(opt.value)}
                    >
                        {opt.label}
                    </li>
                ))}
            </ul>
        </div>,
        document.body,
    );
}

const DataGridImpl = forwardRef<DataGridHandle, DataGridProps>(function DataGrid(
    { name, persistKey, staticFilters, hiddenFilters: extraHiddenFilters, hiddenColumns: extraHiddenColumns, extraColumnInfo, onRowSelect, onButtonClick, editable, onCellEdited, onDataLoaded, autoSearch, compact, toolbarExtra, hideToolbar, chartOnly, chartConfig: chartConfigProp, chartShowLabels, chartAnnotations, onChartMeta, highlightColumn, highlightValue, gridOverride: gridOverrideProp, deleteButton },
    ref,
) {
    // Per-grid sibling-filter channel. Identity-stable across renders so the
    // Provider value doesn't churn. Created lazily on first render via the
    // `useState` initialiser (cheaper than a useRef + dance, and the ref-like
    // identity is preserved because the value is only ever the same object).
    const [siblingStoreRef] = useState(() => ({ current: createFilterSiblingStore() }));

    const [paramFilters, setParamFilters] = useState<InputParam[]>([]);
    const [clearSignal, setClearSignal] = useState(0);
    const [loadSignal, setLoadSignal] = useState(0);
    const [filterBarMode] = useFilterBarMode();
    const [showFilterPanel, setShowFilterPanel] = useState(false);

    const { cache, canRead, canWrite, activeEndpoint } = useAuth();
    const { registerGrid, unregisterGrid } = useSuperAdminPanel();
    const navigate = useNavigate();
    const assistant = useAssistant();

    // Resolve MigratedFilters early so useGridData can remap sColumn names
    const migratedFilters = useMemo(() => {
        if (!cache?.PageInfo) return null;
        const nameLower = name.toLowerCase();
        const pg = cache.PageInfo.Page?.find(
            p => p.Name?.toLowerCase() === nameLower && p.Enabled && p.Available && OPERATION_TYPES.has(p.Type?.toUpperCase()),
        );
        if (!pg) return null;
        const data = cache.PageInfo.Data?.find(d => d.RowID === pg.RowID);
        return data?.MigratedFilters ?? null;
    }, [cache?.PageInfo, name]);

    // §Issue-1: SP grids' GetDataGridHeader EXECs the whole stored procedure just
    // to read the column schema (VIEW/FN grids get a fast TOP 0; an SP can't).
    // When the SP has its columns defined in the cache and isn't a dynamic-column
    // report, synthesize the grid attributes from cache and skip the header call
    // entirely (deferHeaderFetch below). Dynamic-column / column-less SPs still
    // need the header run, so they return null and keep the existing behaviour.
    const cachedSpAttributes = useMemo<GridAttributes | null>(() => {
        if (!cache?.PageInfo) return null;
        const nameLower = name.toLowerCase();
        const pg = cache.PageInfo.Page?.find(
            p => p.Name?.toLowerCase() === nameLower && p.Enabled && p.Available && OPERATION_TYPES.has(p.Type?.toUpperCase()),
        );
        if (!pg || pg.Type?.toUpperCase() !== "SP") return null;
        const data = cache.PageInfo.Data?.find(d => d.RowID === pg.RowID);
        const cols = data?.Columns ?? [];
        if (cols.length === 0) return null;                                   // no defined columns → header must run
        if ((data?.ClientOptions ?? []).some(o => o?.toLowerCase() === "dynamiccolumns")) return null; // dynamic → header must run
        return {
            header: cols,
            hiddenColumns: data?.HiddenColumns ?? [],
            hiddenFilters: data?.HiddenFilters ?? [],
            functionParameters: (data?.Params ?? []).map(p => (p.startsWith("@") ? p.substring(1) : p)),
        };
    }, [cache?.PageInfo, name]);

    // Column-exclusion wire list (Spec C §6.3): null = report default (send nothing —
    // server uses its keep-set); non-null = the user's full visible selection (server
    // expands it with renderer deps + structural columns). Updated only on Columns…
    // modal close; useGridData reads it via ref so changing it alone never refetches.
    // Seeded from the persisted per-grid selection so the prune win applies to the very
    // first fetch; validated against the report definition once the cache resolves.
    const [wireColumnList, setWireColumnList] = useState<string[] | null>(() => loadColumnSelection(name)?.columns ?? null);

    const {
        headerSource,
        headerLoading,
        headerSettled,
        dataSource,
        loading,
        error,
        hasSearched,
        filters,
        sorts,
        page,
        pageSize,
        setFilters,
        setSorts,
        setPage,
        setPageSize,
        search,
        refresh: gridRefresh,
        clear: gridClear,
        knownTotal,
        countLoading,
        countError,
        fetchCount,
        incomplete,
        cancelled,
        cancel: cancelSearch,
        // skipHeader is intentionally false (never the autoSearch flag): skipping the
        // header made an autoSearch grid fetch GetDataGrid immediately with only the
        // static filters, BEFORE the report's parameter defaults (e.g. dStartDate/
        // dEndDate) could resolve — they resolve from the header attributes that the
        // skipped header would have provided. That produced two GetDataGrid calls
        // (premature + corrected). Fetching the lightweight header first lets the
        // params resolve, so the autoSearch effect fires a single, fully-filtered fetch.
    } = useGridData(name, staticFilters, paramFilters, migratedFilters, false, wireColumnList, cachedSpAttributes != null);

    // commitCellValue's real implementation needs performCellSave, which is
    // defined below the `if (!attributes) return ...` early-return further
    // down — so it can't live in this hook call directly (every hook must run
    // unconditionally, above that early-return). Indirect through a ref: the
    // handle always delegates to whatever the ref currently holds, and the
    // ref itself is updated by a plain (non-hook) assignment further down,
    // each render that gets far enough to define performCellSave.
    const commitCellValueRef = useRef<DataGridHandle["commitCellValue"]>(async () => false);
    useImperativeHandle(ref, () => ({
        refresh: gridRefresh,
        search,
        commitCellValue: (ri, col, value) => commitCellValueRef.current(ri, col, value),
    }), [gridRefresh, search]);

    // Reset vertical scroll to the top whenever the user pages through the grid — otherwise
    // the viewport stays wherever it was on the previous page and row 1 of the new page is
    // hidden above the fold. Horizontal scroll is preserved.
    const scrollAreaRef = useRef<HTMLDivElement>(null);
    useEffect(() => {
        scrollAreaRef.current?.scrollTo({ top: 0 });
    }, [page]);

    // ── Filter state persistence (URL hash or sessionStorage depending on setting) ──
    const { initialFilters: persistedFilters, syncFilters, pushFilters, clearStoredFilters, popSignal, popFilters } = useFilterUrlSync({
      gridName: persistKey ?? name,
    });

    // Load filters on mount from persisted state. Auto-search only if coming from a shared link.
    // Splits the restored filters into inline (FilterBar) and advanced (AdvancedFilterBar)
    // based on each column's FilterType.
    const externalFiltersApplied = useRef(false);
    useEffect(() => {
      if (externalFiltersApplied.current) return;
      if (!persistedFilters?.length) return;

      externalFiltersApplied.current = true;

      // Partition: inline text filters go to `filters`, everything else to `paramFilters`
      const inline: InputParam[] = [];
      const advanced: InputParam[] = [];
      for (const p of persistedFilters) {
        const colKey = p.sColumn.replace(/^@/, "").toLowerCase();
        const baseKey = /[01]$/.test(colKey) ? colKey.slice(0, -1) : colKey;
        const info = colInfoMapRef.current[colKey] ?? colInfoMapRef.current[baseKey];
        const ft = info?.FilterType?.toLowerCase() ?? "";
        if (INLINE_FILTER_TYPES.has(ft)) inline.push(p);
        else advanced.push(p);
      }

      if (inline.length > 0) setFilters(inline);
      if (advanced.length > 0) {
        setParamFilters(advanced);
        setLoadSignal(s => s + 1);
      }

      // Check shared link auto-search marker (one-time trigger)
      const autoSearchKey = `sharedlink-autosearch-${name.toLowerCase()}`;
      const shouldAutoSearch = sessionStorage.getItem(autoSearchKey);
      if (shouldAutoSearch) {
        sessionStorage.removeItem(autoSearchKey);
        requestAnimationFrame(() => {
          search();
          pushFilters(persistedFilters);
        });
      }
    }, [name, persistedFilters, search, pushFilters, setFilters]);

    // Persist filter state on every change (not only on search).
    // The hook's syncFilters only writes non-empty state so transient empty states
    // during mount (before external filters are applied) won't wipe stored state.
    useEffect(() => {
      syncFilters([...filters, ...paramFilters]);
    }, [filters, paramFilters, syncFilters]);

    // Handle back/forward navigation (stateful URLs only — hook returns popSignal=0 otherwise)
    useEffect(() => {
      if (popSignal === 0 || !popFilters) return;
      setParamFilters(popFilters);
      setLoadSignal(s => s + 1);
      requestAnimationFrame(() => search());
    }, [popSignal]); // eslint-disable-line react-hooks/exhaustive-deps

    const [showExport, setShowExport] = useState(false);
    const [showScheduleExport, setShowScheduleExport] = useState(false);
    const [showShareDialog, setShowShareDialog] = useState(false);
    const [showColumnConfig, setShowColumnConfig] = useState(false);
    const [showSavedFilters, setShowSavedFilters] = useState(false);
    const [savedFilters, setSavedFilters] = useState<SavedFilter[]>(() => loadSavedFilters(name));
    const [confirmAction, setConfirmAction] = useState<{ rowIdx: number; col: string; row: unknown[]; type: string } | null>(null);
    const [popupPath, setPopupPath] = useState<string | null>(null);
    const [visibleColumns, setVisibleColumns] = useState<string[] | null>(() => loadColumnSelection(name)?.columns ?? null);
    const [columnWidths, setColumnWidths] = useState<Record<string, number>>(() => {
        try {
            const raw = localStorage.getItem(`webadmincc-colwidths-${name.toUpperCase()}`);
            return raw ? JSON.parse(raw) as Record<string, number> : {};
        } catch { return {}; }
    });
    const [selectedRowIdx, setSelectedRowIdx] = useState<number | null>(null);

    const { toast } = useToast();

    /* Scroll a still-filled column filter back into view. The inline filter row
     * lives inside the horizontally-scrolling table, so a value left behind
     * from an earlier search (e.g. one that survived an endpoint switch) can
     * sit far off to the right with nothing on screen to show for it. Keyed by
     * the header name and, for migrated filters, the source column name. */
    const revealColumnFilter = useCallback((sColumn: string) => {
        const key = sColumn.replace(/^@/, "").toLowerCase();
        const esc = typeof CSS !== "undefined" && CSS.escape ? CSS.escape(key) : key.replace(/"/g, '\\"');
        const cell = scrollAreaRef.current?.querySelector<HTMLElement>(
            `[data-filter-col="${esc}"], [data-filter-src="${esc}"]`,
        );
        if (!cell) {
            toast("That column is hidden — its filter is still applied.", "info");
            return;
        }
        cell.scrollIntoView({ block: "nearest", inline: "center", behavior: "smooth" });
        cell.querySelector<HTMLElement>("input, select, button")?.focus({ preventScroll: true });
    }, [toast]);

    // Row context menu (right-click)
    const [rowMenu, setRowMenu] = useState<{
        x: number;
        y: number;
        row: unknown[];
        /** Column of the right-clicked cell, if the click landed on one. */
        column?: string;
        /** Each cell's text as displayed, by column — for the Copy actions. */
        cellTexts: Record<string, string>;
    } | null>(null);

    // Inline editing state
    const [editCell, setEditCell] = useState<{ rowIdx: number; col: string } | null>(null);
    const [editValue, setEditValue] = useState("");
    const [editAnchorEl, setEditAnchorEl] = useState<HTMLElement | null>(null);
    const [cellOverrides, setCellOverrides] = useState<Record<string, unknown>>({});
    const [editSaving, setEditSaving] = useState(false);
    const [cellStates, setCellStates] = useState<Record<string, "saving" | "success" | "error">>({});
    const saveTimeoutRef = useRef<ReturnType<typeof setTimeout>>(undefined);

    const beginSave = useCallback(() => {
        setEditSaving(true);
        clearTimeout(saveTimeoutRef.current);
        saveTimeoutRef.current = setTimeout(() => setEditSaving(false), 10_000);
    }, []);
    const endSave = useCallback(() => {
        clearTimeout(saveTimeoutRef.current);
        setEditSaving(false);
    }, []);
    useEffect(() => () => clearTimeout(saveTimeoutRef.current), []);
    useEffect(() => { setCellOverrides({}); setCellStates({}); }, [dataSource]);

    const setCellState = useCallback((key: string, state: "saving" | "success" | "error") => {
        setCellStates(prev => ({ ...prev, [key]: state }));
        if (state !== "saving") {
            setTimeout(() => setCellStates(prev => {
                const next = { ...prev };
                delete next[key];
                return next;
            }), 4000);
        }
    }, []);

    // Notify parent when grid data loads (for chart rendering)
    const onDataLoadedRef = useRef(onDataLoaded);
    onDataLoadedRef.current = onDataLoaded;
    useEffect(() => {
        if (!dataSource || !onDataLoadedRef.current) return;
        const cols = dataSource.attributes?.header ?? [];
        const rows: Record<string, unknown>[] = [];
        for (const row of dataSource.matrix) {
            if (!Array.isArray(row)) continue;
            const obj: Record<string, unknown> = {};
            for (let i = 0; i < row.length; i++) {
                const colName = cols[i];
                if (colName !== undefined) obj[colName] = row[i];
            }
            rows.push(obj);
        }
        onDataLoadedRef.current(cols, rows);
    }, [dataSource]);

    // Per-page + per-pivot inline column-description overrides for THIS grid. Resolved
    // self-contained from cache (independent of the pageData/pivotData memos below, which
    // are declared later) so the overlay is available to colInfoMap + getHeaderDisplay.
    const { pageColumnDescJson, pivotColumnDescJson } = useMemo(() => {
        if (!cache?.PageInfo) return { pageColumnDescJson: undefined as string | undefined, pivotColumnDescJson: undefined as string | undefined };
        const nameLower = name.toLowerCase();
        let resolved: WebAdminPage | undefined;
        for (const p of cache.PageInfo.Page ?? []) {
            if (p.Name?.toLowerCase() !== nameLower) continue;
            if (!p.Enabled || !p.Available) continue;
            if (!OPERATION_TYPES.has(p.Type?.toUpperCase())) continue;
            resolved = p; // last match wins (highest priority), mirrors pageData resolution
        }
        if (!resolved) return { pageColumnDescJson: undefined, pivotColumnDescJson: undefined };
        const data = cache.PageInfo.Data?.find((d) => d.RowID === resolved!.RowID);
        const pivot = cache.PageInfo.Pivot?.find((p) => p.RowID === resolved!.RowID);
        return { pageColumnDescJson: data?.ColumnDescJson, pivotColumnDescJson: pivot?.ColumnDescJson };
    }, [cache?.PageInfo, name]);

    // Build display name lookup, known-column set, and column info map.
    // Priority (lowest→highest): global cache.ColumnInfo → page ColumnDescJson →
    // pivot ColumnDescJson → extraColumnInfo prop. All lookups case-insensitive (lowercase keys).
    const { displayNames, knownColumns, colInfoMap } = useMemo(() => {
        const map: Record<string, string> = {};
        const known = new Set<string>();
        const infoMap: Record<string, ColumnDescription> = {};
        if (cache?.ColumnInfo) {
            for (const col of cache.ColumnInfo) {
                const key = col.ColumnName.toLowerCase();
                known.add(key);
                // Shallow-clone so per-page/extra overrides never mutate the shared cache object.
                infoMap[key] = { ...col };
                if (col.Header) {
                    map[key] = col.Header;
                }
            }
        }
        // Inline overrides are WHOLE-COLUMN: a column present in the page/pivot JSON is
        // described entirely by that object (fields absent from it render empty/default —
        // there is NO per-field fallback to the shared S_Admin_Column_Desc). Absent columns
        // keep the shared row. Pivot replaces page for the same column (applied last).
        const ovStr = (v: unknown): string => (v === null || v === undefined ? "" : String(v));
        const ovNum = (v: unknown): number => (v === null || v === undefined || v === "" ? 0 : Number(v));
        const applyOverrides = (overrides: Record<string, Partial<ColumnDescription>>) => {
            for (const [key, partial] of Object.entries(overrides)) {
                const entry: ColumnDescription = {
                    ColumnName: partial.ColumnName ?? key,
                    ColumnFormat: ovStr(partial.ColumnFormat),
                    Header: ovStr(partial.Header),
                    DisplayType: ovStr(partial.DisplayType),
                    ToolTip: ovStr(partial.ToolTip),
                    FilterType: ovStr(partial.FilterType),
                    FilterText: ovStr(partial.FilterText),
                    FilterSettings: ovStr(partial.FilterSettings),
                    LinkID: ovNum(partial.LinkID),
                    LinkView: ovStr(partial.LinkView),
                    DefaultWidth: ovNum(partial.DefaultWidth),
                };
                infoMap[key] = entry; // whole-column replace
                known.add(key);
                map[key] = entry.Header;
            }
        };
        applyOverrides(parseColumnDescJson(pageColumnDescJson));
        applyOverrides(parseColumnDescJson(pivotColumnDescJson));
        if (extraColumnInfo) {
            for (const col of extraColumnInfo) {
                const key = col.ColumnName.toLowerCase();
                const existing = infoMap[key];
                if (existing) {
                    // Merge overrides into the existing entry. A caller passing these
                    // fields wants them applied even for server-described columns.
                    if (col.FilterType) existing.FilterType = col.FilterType;
                    if (col.FilterText) existing.FilterText = col.FilterText;
                    if (col.FilterSettings) existing.FilterSettings = col.FilterSettings;
                    if (col.DisplayType) existing.DisplayType = col.DisplayType;
                    if (col.Header) { existing.Header = col.Header; map[key] = col.Header; }
                } else {
                    // Synthesise a full ColumnDescription so downstream consumers
                    // that index into the map don't trip over undefined fields.
                    infoMap[key] = {
                        ColumnName: col.ColumnName,
                        ColumnFormat: col.ColumnFormat ?? "",
                        Header: col.Header ?? "",
                        DisplayType: col.DisplayType ?? "",
                        ToolTip: col.ToolTip ?? "",
                        FilterType: col.FilterType ?? "",
                        FilterText: col.FilterText ?? "",
                        FilterSettings: col.FilterSettings ?? "",
                        LinkID: col.LinkID ?? 0,
                        LinkView: col.LinkView ?? "",
                        DefaultWidth: col.DefaultWidth ?? 0,
                        AdminTS: col.AdminTS,
                        LocalTS: col.LocalTS,
                    };
                    if (col.Header) map[key] = col.Header;
                }
            }
        }
        // Synthetic save/delete action columns (injected into the header when the
        // SaveButton/DeleteButton flags are set). Registered here so colInfoMap
        // lookups resolve their DisplayType; deliberately NOT added to
        // knownColumns so they stay non-sortable / non-filterable.
        for (const synthetic of [
            { name: GENERAL_SAVE_COL, displayType: "COMPONENT_GENERALSAVE" },
            { name: GENERAL_DELETE_COL, displayType: "COMPONENT_GENERALDELETE" },
        ]) {
            infoMap[synthetic.name] = {
                ColumnName: synthetic.name, ColumnFormat: "", Header: "",
                DisplayType: synthetic.displayType, ToolTip: "", FilterType: "",
                FilterText: "", FilterSettings: "", LinkID: 0, LinkView: "",
                DefaultWidth: 35,
            };
        }
        return { displayNames: map, knownColumns: known, colInfoMap: infoMap };
    }, [cache?.ColumnInfo, extraColumnInfo, pageColumnDescJson, pivotColumnDescJson]);

    const DEFAULT_WIDTH = 83;
    const CHAR_WIDTH = 8;
    const HEADER_PADDING = 28; // sort indicator + resize handle + cell padding
    const MAX_SAVED_WIDTH = 500;
    const MIN_WIDTH_EMPTY_HEADER = 40;
    const MIN_WIDTH = 20;

    const columnWidthsRef = useRef(columnWidths);
    columnWidthsRef.current = columnWidths;

    const colInfoMapRef = useRef(colInfoMap);
    colInfoMapRef.current = colInfoMap;

    const getColumnWidth = useCallback((h: string): number => {
        const userWidth = columnWidthsRef.current[h];
        if (userWidth !== undefined) return Math.min(userWidth, MAX_SAVED_WIDTH);
        const info = colInfoMapRef.current[h.toLowerCase()];
        if (!info) return DEFAULT_WIDTH;
        if (!info.Header) return MIN_WIDTH_EMPTY_HEADER;
        const headerW = (info.Header?.length ?? 0) * CHAR_WIDTH + HEADER_PADDING;
        const baseW = info.DefaultWidth ?? DEFAULT_WIDTH;
        return Math.max(baseW, headerW, MIN_WIDTH);
    }, []);

    const resizeRef = useRef<{ col: string; startX: number; startW: number } | null>(null);
    const didResizeRef = useRef(false);

    const handleResizeStart = useCallback((e: React.MouseEvent, col: string) => {
        e.preventDefault();
        e.stopPropagation();
        const startX = e.clientX;
        const startW = getColumnWidth(col);
        resizeRef.current = { col, startX, startW };
        didResizeRef.current = false;

        const onMouseMove = (ev: MouseEvent) => {
            if (!resizeRef.current) return;
            didResizeRef.current = true;
            const { col: resizeCol, startX: rStartX, startW: rStartW } = resizeRef.current;
            const delta = ev.clientX - rStartX;
            const newWidth = Math.max(MIN_WIDTH, rStartW + delta);
            setColumnWidths((prev) => ({ ...prev, [resizeCol]: newWidth }));
        };

        const onMouseUp = () => {
            resizeRef.current = null;
            document.removeEventListener("mousemove", onMouseMove);
            document.removeEventListener("mouseup", onMouseUp);
            // Persist column widths to localStorage
            setColumnWidths(current => {
                try { localStorage.setItem(`webadmincc-colwidths-${name.toUpperCase()}`, JSON.stringify(current)); } catch { /* quota */ }
                return current;
            });
        };

        document.addEventListener("mousemove", onMouseMove);
        document.addEventListener("mouseup", onMouseUp);
    }, [getColumnWidth]);

    /** The title the header row shows for a column; the row menu's question uses the same text. */
    const columnTitle = (h: string): string => {
        const key = h.toLowerCase();
        // Synthetic action columns have no header label.
        if (key === GENERAL_SAVE_COL || key === GENERAL_DELETE_COL) return "";
        if (!knownColumns.has(key)) {
            // Dynamic column not in column definitions — show raw name, not an error
            return h;
        }
        const text = displayNames[key]?.replace(/\\n/, " ") ?? "";
        return text;
    };
    const renderColumnHeader = (h: string): ReactNode => columnTitle(h);

    // Use dataSource attributes when available (handles dynamic/pivot columns),
    // fall back to headerSource for initial column layout before search, then to
    // the cache-synthesized SP attributes (when the header fetch was deferred —
    // see cachedSpAttributes). Once data loads, dataSource.attributes wins.
    const attributes = dataSource?.attributes ?? headerSource?.attributes ?? cachedSpAttributes ?? undefined;

    // Localization grids can expose new language columns before cache.ColumnInfo catches up.
    // Synthesize minimal column metadata for ISO3 headers so they render with friendly names
    // and keep inline filtering available instead of showing raw code-only dynamic columns.
    const gridNameLower = name.toLowerCase();
    if (attributes?.header && gridNameLower.includes("localization")) {
        for (const header of attributes.header) {
            if (!ISO3_LANGUAGE_CODE_RE.test(header)) continue;
            const key = header.toLowerCase();
            if (knownColumns.has(key)) continue;

            const display = resolveLanguageNameFromIso3(header);
            knownColumns.add(key);
            displayNames[key] = display;
            colInfoMap[key] = {
                ColumnName: header,
                ColumnFormat: "",
                Header: display,
                DisplayType: "TEXTBOXVERIFIED",
                ToolTip: display,
                FilterType: "FilterTextBoxLike",
                FilterText: display,
                FilterSettings: "",
                LinkID: 0,
                LinkView: "",
                DefaultWidth: 0,
                AdminTS: 0,
                LocalTS: 0,
            };
        }
    }

    // Reset user column selection when the header set changes (e.g. dynamic/pivot columns).
    // Skipped while a custom wire list is active — then header changes are the expected
    // result of our own column-selection refetch, not a dynamic-column reshuffle.
    const prevHeaderRef = useRef<string[] | undefined>(undefined);
    const currentHeader = attributes?.header;
    if (currentHeader && prevHeaderRef.current && prevHeaderRef.current !== currentHeader && wireColumnList == null) {
        const prev = prevHeaderRef.current.join("\0");
        const curr = currentHeader.join("\0");
        if (prev !== curr) {
            setVisibleColumns(null);
        }
    }
    prevHeaderRef.current = currentHeader;

    // Look up page data matching WebAdmin2's OperationsDic logic:
    // filter to operation types that are enabled/available, pick last match (highest priority), check permission
    const { pageDef, pageData } = useMemo((): { pageDef?: WebAdminPage; pageData?: WebAdminPageData } => {
        if (!cache?.PageInfo) return {};
        const nameLower = name.toLowerCase();
        const pages = cache.PageInfo.Page;
        if (!pages) return {};
        let resolved: typeof pages[number] | undefined;
        for (const p of pages) {
            if (p.Name?.toLowerCase() !== nameLower) continue;
            if (!p.Enabled || !p.Available) continue;
            if (!OPERATION_TYPES.has(p.Type?.toUpperCase())) continue;
            resolved = p;
        }
        if (!resolved || !canRead(resolved.RowID)) return {};
        const data = cache.PageInfo.Data?.find((d) => d.RowID === resolved!.RowID);
        return { pageDef: resolved, pageData: data ? applyClientOptionFlags(data) : data };
    }, [cache?.PageInfo, name, canRead]);

    // Report-defined column names (from the cached page definition) — the full set the
    // report can return, independent of what the current (possibly pruned) fetch contains.
    const definedColumns = useMemo(() => {
        const cols = pageData?.Columns as unknown;
        return Array.isArray(cols) ? (cols as string[]).filter(Boolean)
            : typeof cols === "string" ? cols.split(",").map((s) => s.trim()).filter(Boolean)
            : [];
    }, [pageData?.Columns]);

    // Signature of the report's default visible set (cache-derived, stable across
    // pruned/unpruned fetches) — stored with a persisted column selection so a changed
    // report definition invalidates it.
    const columnDefSignature = useMemo(() => {
        const hidden = new Set((pageData?.HiddenColumns ?? []).map((c) => c.toLowerCase()));
        return definedColumns.filter((c) => !hidden.has(c.toLowerCase())).map((c) => c.toLowerCase()).join("|");
    }, [definedColumns, pageData?.HiddenColumns]);

    // Apply a column selection (null = report default) to state + per-grid persistence.
    // Does NOT decide about refetching — callers handle that (syncWireColumns / search()).
    const applyColumnSelection = useCallback((cols: string[] | null) => {
        const list = cols && cols.length > 0 ? cols : null;
        setVisibleColumns(list);
        setWireColumnList(list);
        if (list) storeColumnSelection(name, list, columnDefSignature);
        else clearColumnSelection(name);
    }, [name, columnDefSignature]);

    // Validate the persisted selection once the report definition is available: if the
    // report's default columns changed since it was stored, drop it (a stale list could
    // hide new columns or reference removed ones) and tell the user why.
    useEffect(() => {
        if (!pageData) return;
        const stored = loadColumnSelection(name);
        if (!stored) return;
        if (stored.def !== columnDefSignature) {
            applyColumnSelection(null);
            gridRefresh(); // discard any fetch made with the stale list (no-op pre-search)
            toast("Saved column selection was reset — the report's columns changed", "info");
        }
    }, [name, pageData, columnDefSignature, applyColumnSelection, gridRefresh, toast]);

    // Pivot config: when present, headers contain pivoted values (e.g. one
    // column per language) instead of the underlying value column. Cell edits
    // must translate to (PivotHeaderColumn=<header>, Equal) + (PivotValueColumn=<new>, Set).
    const pivotData = useMemo(() => {
        const pivots = cache?.PageInfo?.Pivot;
        if (!Array.isArray(pivots) || pageData?.RowID == null) return undefined;
        return pivots.find((p) => p.RowID === pageData.RowID);
    }, [cache?.PageInfo?.Pivot, pageData?.RowID]);

    // Editable if explicitly set via prop, otherwise auto-detect from write permission
    const isEditable = editable ?? (pageDef ? canWrite(pageDef.RowID) : false);

    // Register grid info for SuperAdmin Page Information tool
    useEffect(() => {
        registerGrid({ name, pageDef, pageData, attributes, colInfoMap });
        return () => unregisterGrid(name);
    }, [name, pageDef, pageData, attributes, colInfoMap, registerGrid, unregisterGrid]);

    // Check if this grid should also display as a chart
    const chartConfig = useMemo(() => {
        const base = chartConfigProp ?? parseChartConfig(pageData)?.[0] ?? null;
        if (base && chartShowLabels !== undefined) return { ...base, showLabels: chartShowLabels };
        return base;
    }, [pageData, chartConfigProp, chartShowLabels]);

    // Compute parameter names for advanced filter bar
    const paramNames = useMemo(() => {
        const raw = pageData?.Params as unknown;
        const fromPage = typeof raw === "string" ? raw.split(",").map((s: string) => s.trim()).filter(Boolean)
                       : Array.isArray(raw) ? raw as string[] : [];
        const fromHeader = Array.isArray(attributes?.functionParameters) ? attributes.functionParameters : [];
        return [...new Set([...fromPage, ...fromHeader])];
    }, [pageData?.Params, attributes?.functionParameters]);

    // Auto-search: trigger once when header is ready.  When the grid has
    // parameter-based advanced filters (e.g. ComboBoxFilter) we wait for
    // their default values to be emitted before firing the first search.
    const autoSearchFired = useRef(false);

    const hasResolvableParamFilters = useMemo(() => {
        // Params already supplied via staticFilters don't need to be resolved
        const staticCols = new Set(
            (staticFilters ?? []).map(f => {
                const c = f.sColumn ?? "";
                return (c.startsWith("@") ? c.substring(1) : c).toLowerCase();
            }),
        );
        for (const p of paramNames) {
            const stripped = p.startsWith("@") ? p.substring(1) : p;
            if (staticCols.has(stripped.toLowerCase())) continue;
            const info = colInfoMap[stripped.toLowerCase()];
            if (info?.FilterType && getFilter(info.FilterType)) return true;
        }
        if (extraColumnInfo) {
            for (const col of extraColumnInfo) {
                if (col.FilterType && getFilter(col.FilterType)) return true;
            }
        }
        return false;
    }, [paramNames, colInfoMap, extraColumnInfo, staticFilters]);

    useEffect(() => {
        // Wait for the header phase to finish. headerLoading alone isn't enough: it is
        // false on the first render, before the header fetch starts, so the search fired
        // early, and in dev StrictMode's re-mount then reset hasSearched while the
        // fired-once ref stayed set, leaving the grid empty.
        if (!autoSearch || autoSearchFired.current || headerLoading || !headerSettled) return;
        // In compact mode, filters are hidden — don't wait for param filter resolution
        if (!compact && hasResolvableParamFilters && paramFilters.length === 0) return;
        autoSearchFired.current = true;
        search();
    }, [autoSearch, headerLoading, headerSettled, search, hasResolvableParamFilters, paramFilters, compact]);

    // Wrapper that searches and pushes filter state to history
    const searchAndPush = useCallback(() => {
      search();
      pushFilters([...filters, ...paramFilters]);
    }, [search, pushFilters, filters, paramFilters]);

    // Apply the saved-default filter (if any) to both column and param state.
    // Returns true if a default existed and was applied.
    const applyDefaultFilter = useCallback((loaded: SavedFilter[]): boolean => {
        const defaultRaw = loaded.find(f => f.isDefault);
        if (!defaultRaw) return false;
        const defaultFilter = resolveRelativeDates(defaultRaw);
        setFilters(defaultFilter.filters ?? []);
        setParamFilters(defaultFilter.paramFilters ?? []);
        // Views saved before column selections existed (columns === undefined) leave
        // the current selection alone; newer views always carry null-or-list.
        if (defaultFilter.columns !== undefined) applyColumnSelection(defaultFilter.columns);
        setLoadSignal(s => s + 1);
        return true;
    }, [setFilters, applyColumnSelection]);

    const clear = useCallback(() => {
        gridClear();
        setParamFilters([]);
        setClearSignal((s) => s + 1);
        clearStoredFilters();
        // Re-apply the saved default so "Clear" means "back to my saved view",
        // not "empty everything"
        applyDefaultFilter(savedFilters);
    }, [gridClear, clearStoredFilters, applyDefaultFilter, savedFilters]);

    // Saved filters: load default on mount. Skip applying it when the
    // persisted-state restore (sessionStorage / URL hash) has filters to
    // hand back — the user's most recent active state should win over the
    // static saved default view, otherwise refreshing a page that has a
    // default view always wipes any in-progress filtering.
    useEffect(() => {
        const loaded = loadSavedFilters(name);
        setSavedFilters(loaded);
        if (persistedFilters?.length) return;
        applyDefaultFilter(loaded);
    }, [name, applyDefaultFilter, persistedFilters]);

    const handleLoadFilter = useCallback((filter: SavedFilter) => {
        const resolved = resolveRelativeDates(filter);
        setFilters(resolved.filters);
        setParamFilters(resolved.paramFilters);
        // Restore the view's column selection (search() below refetches with it).
        if (resolved.columns !== undefined) applyColumnSelection(resolved.columns);
        setLoadSignal(s => s + 1);
        setShowSavedFilters(false);
        search();
    }, [setFilters, search, applyColumnSelection]);

    // Build case-insensitive set combining hidden columns from both data source and page cache
    const hiddenColumnsSet = useMemo(() => {
        const set = new Set(attributes?.hiddenColumns?.map((c) => c.toLowerCase()));
        if (pageData?.HiddenColumns) {
            for (const c of pageData.HiddenColumns) {
                set.add(c.toLowerCase());
            }
        }
        if (extraHiddenColumns) {
            for (const c of extraHiddenColumns) {
                set.add(c.toLowerCase());
            }
        }
        return set;
    }, [attributes?.hiddenColumns, pageData?.HiddenColumns, extraHiddenColumns]);

    // Build case-insensitive set combining hidden filters from both data source and page cache
    // Columns explicitly provided via extraColumnInfo are never hidden (caller wants them shown)
    const hiddenFiltersSet = useMemo(() => {
        const forceShow = new Set<string>();
        if (extraColumnInfo) {
            for (const c of extraColumnInfo) {
                forceShow.add(c.ColumnName.toLowerCase());
                if (c.Header) forceShow.add(c.Header.toLowerCase());
                if (c.FilterText) forceShow.add(c.FilterText.toLowerCase());
            }
        }
        const set = new Set<string>();
        if (attributes?.hiddenFilters) {
            for (const c of attributes.hiddenFilters) {
                const lc = c.toLowerCase();
                if (!forceShow.has(lc)) set.add(lc);
            }
        }
        if (pageData?.HiddenFilters) {
            for (const c of pageData.HiddenFilters) {
                const lc = c.toLowerCase();
                if (!forceShow.has(lc)) set.add(lc);
            }
        }
        if (extraHiddenFilters) {
            for (const c of extraHiddenFilters) {
                set.add(c.toLowerCase());
            }
        }
        return set;
    }, [attributes?.hiddenFilters, pageData?.HiddenFilters, extraHiddenFilters, extraColumnInfo]);

    // Filter universe = the page's full declared columns (pageData.Columns) ∪ the returned
    // header ∪ extraColumnInfo. Filters are offered purely on COLUMN CONFIG (FilterType) +
    // explicit hiddenFilters — NEVER on whether a column is shown/returned. A declared column
    // the SQL head omits (not selected, or hidden) keeps its filter; it just renders in the
    // "+ Filter" advanced bar instead of inline (see advancedPromoteSet). Header first so the
    // shown columns keep their existing order.
    const advancedColumns = useMemo(() => {
        const out: string[] = [];
        const seen = new Set<string>();
        const add = (c?: string) => {
            if (!c) return;
            const lc = c.toLowerCase();
            if (seen.has(lc)) return;
            seen.add(lc);
            out.push(c);
        };
        for (const c of (attributes?.header ?? [])) add(c);
        for (const c of (pageData?.Columns ?? [])) add(c);
        if (extraColumnInfo) for (const c of extraColumnInfo) add(c.ColumnName);
        return out;
    }, [attributes?.header, pageData?.Columns, extraColumnInfo]);

    // Columns whose (inline) filter must be PROMOTED to the advanced bar because the column
    // isn't rendered inline — either flagged hidden, or declared-but-not-returned by the SQL
    // head. This is what stops a filterable column the query omits from silently losing its
    // filter. Passed to AdvancedFilterBar as its hiddenColumnsSet (its only consumer is the
    // promotion path).
    const advancedPromoteSet = useMemo(() => {
        const shown = new Set((attributes?.header ?? []).map((c) => c.toLowerCase()));
        const set = new Set(hiddenColumnsSet);
        for (const c of advancedColumns) {
            const lc = c.toLowerCase();
            if (!shown.has(lc)) set.add(lc);
        }
        return set;
    }, [advancedColumns, attributes?.header, hiddenColumnsSet]);

    // Hide "+ Filter" when nothing could be added: mirrors the resolved-filter
    // logic in AdvancedFilterBar so the button only appears when at least one
    // filter would actually render in the panel.
    const hasAnyFilter = useMemo(() => {
        const migrated = pageData?.MigratedFilters;
        const isHidden = (lc: string, info?: ColumnDescription | null) => {
            if (hiddenFiltersSet.has(lc)) return true;
            if (!info) return false;
            if (info.Header && hiddenFiltersSet.has(info.Header.toLowerCase())) return true;
            if (info.FilterText && hiddenFiltersSet.has(info.FilterText.toLowerCase())) return true;
            return false;
        };
        const seen = new Set<string>();
        // 1. Function parameters
        for (const p of paramNames) {
            const stripped = p.startsWith("@") ? p.substring(1) : p;
            const lc = stripped.toLowerCase();
            const uc = stripped.toUpperCase();
            if (seen.has(lc)) continue;
            if (migrated && typeof migrated === "object" && uc in migrated && migrated[uc] === null) continue;
            const info = colInfoMap[lc];
            if (!info?.FilterType) continue;
            if (isHidden(lc, info)) continue;
            if (!getFilter(info.FilterType)) continue;
            return true;
        }
        // 2. Columns with parameterOnly filters (over the full filter universe).
        for (const col of advancedColumns) {
            const lc = col.toLowerCase();
            if (seen.has(lc)) continue;
            const info = colInfoMap[lc];
            if (!info?.FilterType) continue;
            if (isHidden(lc, info)) continue;
            const reg = getFilter(info.FilterType);
            if (!reg?.parameterOnly) continue;
            return true;
        }
        // 3. Columns not shown inline (hidden, or omitted by the SQL head) whose filters are
        //    NOT hidden — promoted to the advanced bar.
        for (const col of advancedColumns) {
            const lc = col.toLowerCase();
            const uc = col.toUpperCase();
            if (seen.has(lc)) continue;
            if (!advancedPromoteSet.has(lc)) continue;
            if (migrated && typeof migrated === "object" && uc in migrated && migrated[uc] === null) continue;
            const info = colInfoMap[lc];
            if (!info?.FilterType) continue;
            if (isHidden(lc, info)) continue;
            if (!getFilter(info.FilterType)) continue;
            return true;
        }
        return false;
    }, [paramNames, colInfoMap, hiddenFiltersSet, advancedColumns, advancedPromoteSet, pageData?.MigratedFilters]);

    // Columns whose filters are rendered in the advanced bar (not inline)
    const advancedColumnsSet = useMemo(
        () => getAdvancedColumnKeys(advancedColumns, colInfoMap, hiddenFiltersSet),
        [advancedColumns, colInfoMap, hiddenFiltersSet],
    );

    // Derive footer column config from session cache (backend doesn't set attributes.summaryColumns)
    const footerColumnMap = useMemo(() => {
        const map = new Map<string, { index: number; showSum: boolean; showAvg: boolean }>();
        if (!cache?.PageInfo) return map;

        const nameLower = name.toLowerCase();
        const pg = cache.PageInfo.Page?.find(
            p => p.Name?.toLowerCase() === nameLower && p.Enabled && p.Available && OPERATION_TYPES.has(p.Type?.toUpperCase()),
        );
        if (!pg) return map;
        const pd = cache.PageInfo.Data?.find(d => d.RowID === pg.RowID);
        const cols = pd?.FooterColumns;
        if (!cols || cols.length === 0) return map;

        cols.forEach((raw, i) => {
            let col = raw;
            let showSum = true;
            let showAvg = true;
            if (col.startsWith("!")) { showSum = false; col = col.substring(1); }
            else if (col.startsWith("\u00A1")) { showAvg = false; col = col.substring(1); } // ¡
            map.set(col.trim().replace(/^\[|\]$/g, "").toLowerCase(), { index: i, showSum, showAvg });
        });
        return map;
    }, [cache?.PageInfo, name]);

    // Per-grid overrides (cell decorators, commit intercept). Must run on every
    // render — sits above the headerLoading early-return so the hook order is
    // stable when `attributes` flips between undefined and present. Registry
    // builders receive runtime deps (dialog, toast, refresh, cache) since most
    // overrides need to prompt or refresh the grid; the prop wins per key.
    const { choose, confirm } = useAlertDialog();
    const registryOverride: GridOverride | undefined = useMemo(() => {
        const builder = getGridOverrideBuilder(name);
        if (!builder) return undefined;
        return builder({ choose, toast, refresh: gridRefresh, cache, openPopup: setPopupPath });
    }, [name, choose, toast, gridRefresh, cache]);
    const gridOverride: GridOverride | undefined = useMemo(() => {
        if (!registryOverride && !gridOverrideProp) return undefined;
        return {
            cellDecorators: { ...registryOverride?.cellDecorators, ...gridOverrideProp?.cellDecorators },
            cellConfirms: { ...registryOverride?.cellConfirms, ...gridOverrideProp?.cellConfirms },
            cellLocked: { ...registryOverride?.cellLocked, ...gridOverrideProp?.cellLocked },
            onCellEditCommit: gridOverrideProp?.onCellEditCommit ?? registryOverride?.onCellEditCommit,
        };
    }, [registryOverride, gridOverrideProp]);

    const getComboOptions = useCallback((info: ColumnDescription | undefined): { value: string; label: string }[] => {
        if (!info || !cache?.CommonData?.Common) return [];
        const format = info.ColumnFormat ?? "";
        const lookupKey = format ? (format.split("|")[0] ?? info.ColumnName) : info.ColumnName;
        const keyLower = lookupKey.toLowerCase();
        const common = cache.CommonData.Common as { sName?: string; sFilterValue?: string; sDisplayText?: string; TypeName?: string; Value?: string; Title?: string }[];
        const getName = (c: typeof common[0]) => c.TypeName ?? c.sName ?? "";
        const getValue = (c: typeof common[0]) => c.Value ?? c.sFilterValue ?? "";
        const getTitle = (c: typeof common[0]) => c.Title ?? c.sDisplayText ?? "";
        return common
            .filter(item => getName(item).toLowerCase() === keyLower)
            .sort((a, b) => (getTitle(a)).localeCompare(getTitle(b)))
            .map(item => ({ value: getValue(item), label: getTitle(item) || getValue(item) }));
    }, [cache?.CommonData?.Common]);

    if (!attributes) {
        return headerLoading
            ? <div className={styles.pageStatus}><div className={styles.loadingSpinner} /><span>Loading...</span></div>
            : error
                ? <Alert variant="error" title="Couldn't load report"><div className={styles.gridMessage}>{error}</div></Alert>
                : <EmptyState icon={<StatusIcon kind="search" size={32} />} title="No data" />;
    }

    // Initialize visible columns from attributes
    const baseVisible =
        visibleColumns ??
        attributes.header.filter((h) => !hiddenColumnsSet.has(h.toLowerCase()));
    // Append synthetic action columns (SaveButton/DeleteButton ClientOptions).
    // Save first, then delete — matches WebAdmin2 column order.
    const effectiveVisible = (() => {
        const out = [...baseVisible];
        if (pageData?.SaveButton && !out.includes(GENERAL_SAVE_COL)) out.push(GENERAL_SAVE_COL);
        if ((deleteButton || pageData?.DeleteButton) && !out.includes(GENERAL_DELETE_COL)) out.push(GENERAL_DELETE_COL);
        return out;
    })();

    // Columns to export = currently visible columns minus the synthetic action columns.
    const exportColumns = effectiveVisible.filter(
        (c) => c !== GENERAL_SAVE_COL && c !== GENERAL_DELETE_COL,
    );

    // Footer (SUM/AVG) rows only make sense while at least one footer column is visible —
    // when the user excludes them all, the server NULLs the aggregates and the rows would
    // render as empty "SUM:/AVG:" labels.
    const hasVisibleFooter = effectiveVisible.some((h) => footerColumnMap.has(h.toLowerCase()));

    // Columns… chooser set: the fetched header plus report-defined columns the server
    // pruned from this fetch (Spec C) — those stay choosable; picking one triggers a
    // refetch with an explicit columnList on modal close. Not extended for pivot/dynamic
    // grids (their headers are generated; the server ignores columnList for them).
    const isDynamicHeader = (attributes.pivotColumnDataType?.length ?? 0) > 0
        || (attributes.pivotGeneratedColumns?.length ?? 0) > 0;
    const chooserColumns = (() => {
        if (isDynamicHeader || definedColumns.length === 0) return attributes.header;
        const fetched = new Set(attributes.header.map((h) => h.toLowerCase()));
        const missing = definedColumns.filter((c) => !fetched.has(c.toLowerCase()));
        return missing.length > 0 ? [...attributes.header, ...missing] : attributes.header;
    })();

    // The report's default visible set (chooser columns minus hidden) — "Default" target
    // and the baseline the wire protocol compares against.
    const chooserDefaultVisible = chooserColumns.filter((c) => !hiddenColumnsSet.has(c.toLowerCase()));

    // §6.3 export wire protocol: when the export selection equals the report's default
    // visible set (case-insensitive set equality), the export payload builders omit
    // ColumnList so the server serves its memoized exportDefault keep-set.
    const isDefaultExportSelection =
        exportColumns.length === chooserDefaultVisible.length &&
        (() => {
            const s = new Set(chooserDefaultVisible.map((c) => c.toLowerCase()));
            return exportColumns.every((c) => s.has(c.toLowerCase()));
        })();

    // Column tooltips (sColumnToolTip) shown directly in the Columns… chooser.
    const chooserTooltips = (() => {
        const out: Record<string, string> = {};
        for (const c of chooserColumns) {
            const tip = colInfoMap[c.toLowerCase()]?.ToolTip;
            if (tip) out[c.toLowerCase()] = tip;
        }
        return out;
    })();

    // TEXTCOLLECTION composites render sibling columns inside their widget — group those
    // siblings under the composite in the chooser (same ¤/§ LinkView format as
    // renderTextCollection: field[0] is layout config, parts[2] of each field = sibling).
    const chooserGroups = (() => {
        const out: Record<string, string[]> = {};
        const present = new Set(chooserColumns.map((c) => c.toLowerCase()));
        for (const c of chooserColumns) {
            const info = colInfoMap[c.toLowerCase()];
            if ((info?.DisplayType ?? "").toUpperCase() !== "TEXTCOLLECTION" || !info?.LinkView) continue;
            const subs: string[] = [];
            const fields = info.LinkView.split(/[¤§]/);
            for (let i = 1; i < fields.length; i++) {
                const sub = (fields[i]?.split("|")[2] ?? "").trim();
                if (sub && sub.toLowerCase() !== c.toLowerCase() && present.has(sub.toLowerCase()) && !subs.some((s) => s.toLowerCase() === sub.toLowerCase())) {
                    subs.push(sub);
                }
            }
            if (subs.length > 0) out[c] = subs;
        }
        return out;
    })();

    // §6.3 wire protocol sync: selection equal to the report default → send nothing;
    // otherwise send the full visible list. Refetch only when the new selection needs a
    // column the current data doesn't have (removing/re-showing fetched columns stays
    // instant and client-side).
    const syncWireColumns = (sel: string[] | null) => {
        const isDefault = sel == null || (
            sel.length === chooserDefaultVisible.length &&
            (() => { const s = new Set(sel.map((c) => c.toLowerCase())); return chooserDefaultVisible.every((c) => s.has(c.toLowerCase())); })()
        );
        const newList = isDefault ? null : sel;
        const changed = (newList == null) !== (wireColumnList == null)
            || (newList != null && wireColumnList != null && newList.join("\0") !== wireColumnList.join("\0"));
        if (!changed) return;
        applyColumnSelection(newList);
        if (!hasSearched) return;
        const fetched = new Set(attributes.header.map((h) => h.toLowerCase()));
        const needsRefetch = (newList ?? chooserDefaultVisible).some((c) => !fetched.has(c.toLowerCase()));
        if (needsRefetch) gridRefresh();
    };

    // Columns… modal close = the one batching point of the wire protocol. An empty
    // selection is never sent (the server would treat it as "default"): the wire list is
    // left untouched and the grid body shows a "no columns" state with a reset action.
    const closeColumnConfig = () => {
        setShowColumnConfig(false);
        if (visibleColumns != null && visibleColumns.length === 0) return;
        syncWireColumns(visibleColumns);
    };

    const resetColumnSelection = () => {
        syncWireColumns(null);
        setVisibleColumns(null);
    };

    // Build column index map
    let colIndex: Record<string, number> = {};
    attributes.header.forEach((h, i) => {
        colIndex[h] = i;
    });
    // DEV-only column-prune guard (Spec C): if the server returned fewer columns than the
    // report defines (server-side column exclusion), warn when any code reads/looks-up a
    // column that was pruned — surfaces a missed keep-set dependency during testing. Fires
    // ONLY for columns the report defines but the server omitted (not for dispatcher
    // fallback names that simply don't apply to this grid). Zero-cost in release.
    if (import.meta.env.DEV) {
        const present = new Set(Object.keys(colIndex).map((k) => k.toLowerCase()));
        const pruned = new Set(definedColumns.map((c) => c.toLowerCase()).filter((c) => c && !present.has(c)));
        // A column the user just checked in Columns… is expected-missing until the
        // modal-close refetch lands — that's chooser state, not a keep-set dep bug.
        for (const c of baseVisible) pruned.delete(c.toLowerCase());
        if (pruned.size > 0) {
            const warned = new Set<string>();
            const note = (p: string | symbol) => {
                if (typeof p !== "string") return;
                const k = p.toLowerCase();
                if (pruned.has(k) && !warned.has(k)) {
                    warned.add(k);
                    console.warn(`[grid-prune-guard] ${name}: read of server-pruned column "${p}" — if it's a dependency, add it to the keep-set dep table (Spec C §5).`);
                }
            };
            colIndex = new Proxy(colIndex, {
                get(t, p) { note(p); return Reflect.get(t, p); },
                has(t, p) { note(p); return Reflect.has(t, p); },
            });
        }
    }

    // --- Stripe grouping by RowCountColumn ---
    const rowCountCol = pageData?.RowCountColumn;
    const rowCountColIdx = rowCountCol ? (colIndex[rowCountCol] ?? -1) : -1;

    // --- Inline editing helpers ---
    const getCellValue = (ri: number, col: string, original: unknown): unknown => {
        const key = `${ri}:${col}`;
        return key in cellOverrides ? cellOverrides[key] : original;
    };

    const resolveCommit = async (
        row: unknown[],
        col: string,
        newValue: unknown,
        oldValue: unknown,
    ): Promise<CellCommitResult> => {
        if (!gridOverride?.onCellEditCommit) return { kind: "default" };
        const keyColumns = attributes.updateKeyColumns ?? pageData?.KeyColumns ?? [];
        const r = await gridOverride.onCellEditCommit({
            gridName: name,
            rowData: row,
            colIndex,
            keyColumns,
            keyFilter: buildKeyFilter(row, col),
            colName: col,
            newValue,
            oldValue,
        });
        return r ?? { kind: "default" };
    };

    const performCellSave = async (
        row: unknown[],
        col: string,
        ri: number,
        newValue: string,
        oldValue: string,
    ): Promise<boolean> => {
        const cellKey = `${ri}:${col}`;

        // SaveButton grids stage edits in-place — a row-level save button
        // commits to the server. Mirrors WebAdmin2 onUpdatedCell's early
        // `if (HeaderData.SaveButton) return true;`. Hook isn't consulted
        // since the contract is "no auto-save on this grid, ever".
        if (pageData?.SaveButton) {
            setCellOverrides((prev) => ({ ...prev, [cellKey]: newValue }));
            setCellState(cellKey, "success");
            onCellEdited?.();
            return true;
        }

        const decision = await resolveCommit(row, col, newValue, oldValue);
        if (decision.kind === "skip") return false;

        // Local-only: update the cell display without an API call. Used for
        // staging-style grids (CompuShopRestock) where edits are consumed
        // wholesale by a separate "Create" button.
        if (decision.kind === "local") {
            const localValue = decision.value !== undefined ? decision.value : newValue;
            setCellOverrides((prev) => ({ ...prev, [cellKey]: localValue }));
            setCellState(cellKey, "success");
            onCellEdited?.();
            return true;
        }

        // Default path with no value change: hook ran but didn't override —
        // suppress the redundant UpdateGridCell round-trip.
        if (decision.kind === "default" && newValue === oldValue) return false;
        const valueToSend = decision.kind === "replace" ? decision.value : newValue;
        const extra = decision.kind === "replace" && decision.extraParams ? decision.extraParams : [];

        beginSave();
        setCellState(cellKey, "saving");
        try {
            const keyParams = getKeyParams(row, { col, value: valueToSend });
            // Pivot mode: the displayed header (e.g. "ARB") isn't the column to
            // SET — it's a value of PivotHeaderColumn. Translate to
            // (PivotHeaderColumn=col, Equal) + (PivotValueColumn=newValue, Set).
            const setEntries: InputParam[] = pivotData
                ? [
                    { sColumn: pivotData.PivotHeaderColumn, Type: ParamType.Equal, oValue: col },
                    { sColumn: pivotData.PivotValueColumn, Type: ParamType.Set, oValue: valueToSend },
                ]
                : [{ sColumn: col, oValue: valueToSend, Type: ParamType.Set }];
            const result = await updateGridCell(name, [
                ...keyParams,
                ...setEntries,
                ...extra,
            ]);
            // UpdateGridCell's oData is the SQL UPDATE's own success flag (row count > 0),
            // not just "no exception was thrown". A cell showing an inherited value with no
            // override row yet has nothing for the UPDATE's WHERE clause to match — the call
            // returns cleanly with bError:false but oData:false, and the edit never actually
            // persists even though nothing failed loudly. Skipping this check silently drops
            // the write while the UI reports success (matches cellOverrides optimistically,
            // reverting only on the next real refetch).
            if (!result.bError && result.oData) {
                setCellOverrides(prev => ({ ...prev, [cellKey]: valueToSend }));
                setCellState(cellKey, "success");
                onCellEdited?.();
                // RefreshOnUpdate grids re-fetch so derived columns and pivoted
                // values stay consistent with the just-saved cell.
                if (pageData?.RefreshOnUpdate) gridRefresh();
                return true;
            }
            setCellState(cellKey, "error");
            toast(
                !result.bError
                    ? "No matching row to update — nothing was saved."
                    : (result.sMessage || "Failed to update cell"),
                "error",
            );
            return false;
        } catch {
            setCellState(cellKey, "error");
            toast("Failed to update cell", "error");
            return false;
        } finally {
            endSave();
        }
    };

    /** Imperative counterpart to double-click cell editing — used by callers
     *  (e.g. a row-edit modal) that already hold a value to commit, without
     *  going through editCell/editValue state. Reuses performCellSave so
     *  pivot handling and gridOverride hooks (e.g. the Localization
     *  "remove override vs. keep empty" prompt) behave identically. Plain
     *  assignment (not a hook) — the actual useImperativeHandle call lives
     *  above the `if (!attributes) return ...` early-return and delegates
     *  through commitCellValueRef, so it always runs the same every render. */
    commitCellValueRef.current = async (ri: number, col: string, newValue: string): Promise<boolean> => {
        if (!dataSource) return false;
        const row = dataSource.matrix[ri];
        if (!row) return false;
        const idx = colIndex[col] ?? -1;
        const oldValue = String(getCellValue(ri, col, idx >= 0 ? row[idx] : "") ?? "");
        if (newValue === oldValue) return true;
        return performCellSave(row, col, ri, newValue, oldValue);
    };

    const getKeyParams = (row: unknown[], override?: { col: string; value: unknown }): InputParam[] => {
        const keyColumns = attributes.updateKeyColumns ?? pageData?.KeyColumns ?? [];
        // Pivot mode: PivotHeaderColumn isn't a real grid header (its values are
        // pivoted into headers like "ARB"/"ENG"); skip it here and append the
        // mapped (PivotHeaderColumn=<edited header>, Equal) entry separately.
        const pivotHeader = pivotData?.PivotHeaderColumn?.toUpperCase();
        return keyColumns
            .filter((kc) => !pivotHeader || kc.toUpperCase() !== pivotHeader)
            .map((kc) => ({
                sColumn: kc,
                oValue: kc === override?.col ? override.value : row[colIndex[kc] ?? -1] ?? "",
                Type: ParamType.Equal,
            }));
    };

    /** Build the full key filter (Equal-only, no Set) used by overrides for
     *  delete-row APIs. In pivot mode appends the PivotHeaderColumn entry so
     *  the filter uniquely identifies the underlying row. */
    const buildKeyFilter = (row: unknown[], col: string): InputParam[] => {
        const params = getKeyParams(row);
        if (pivotData) {
            params.push({
                sColumn: pivotData.PivotHeaderColumn,
                Type: ParamType.Equal,
                oValue: col,
            });
        }
        return params;
    };

    /** COMPONENT_GENERALSAVE button: commit a SaveButton-grid row. Edits stage in
     *  cellOverrides (no auto-save). These Edit SPs upsert the whole row from its
     *  KeyColumns — the server injects @IsDelete=0 only when NO Set value is sent
     *  (DeleteOrUpdateGridCell). So send every KeyColumn as Equal (staged value
     *  where edited, else current row value) and NO Set, mirroring WebAdmin2
     *  SaveItem; then RefreshOnUpdate refetches. */
    const handleRowSave = async (rowIdx: number, row: unknown[]) => {
        if (editSaving) return;
        const keyColumns = attributes.updateKeyColumns ?? pageData?.KeyColumns ?? [];
        if (keyColumns.length === 0) { toast("Grid has no key columns to save", "error"); return; }
        const prefix = `${rowIdx}:`;
        const editedKeys = Object.keys(cellOverrides).filter((k) => k.startsWith(prefix));
        if (editedKeys.length === 0) { toast("No changes to save", "info"); return; }
        const params: InputParam[] = keyColumns.map((kc) => {
            const ovKey = `${prefix}${kc}`;
            const idx = colIndex[kc] ?? -1;
            const v = ovKey in cellOverrides ? cellOverrides[ovKey] : (idx >= 0 ? row[idx] : "");
            return { sColumn: kc, oValue: v ?? "", Type: ParamType.Equal };
        });
        beginSave();
        editedKeys.forEach((k) => setCellState(k, "saving"));
        try {
            const result = await updateGridCell(name, params);
            if (!result.bError) {
                editedKeys.forEach((k) => setCellState(k, "success"));
                onCellEdited?.();
                if (pageData?.RefreshOnUpdate) gridRefresh();
            } else {
                editedKeys.forEach((k) => setCellState(k, "error"));
                toast("Failed to save row", "error");
            }
        } catch {
            editedKeys.forEach((k) => setCellState(k, "error"));
            toast("Failed to save row", "error");
        } finally {
            endSave();
        }
    };

    /** COMPONENT_GENERALDELETE button: confirm, then DeleteGridCell keyed by the
     *  row's KeyColumns, then refresh the grid. */
    const handleRowDelete = async (row: unknown[]) => {
        if (editSaving) return;
        if (!(await confirm({ title: "Delete this row?", body: "This cannot be undone.", confirmLabel: "Delete", destructive: true }))) return;
        beginSave();
        try {
            const result = await deleteGridCell(name, getKeyParams(row));
            if (!result.bError && result.oData) {
                toast("Row deleted", "success");
                gridRefresh();
            } else {
                toast(result.sMessage || "Failed to delete row", "error");
            }
        } catch {
            toast("Failed to delete row", "error");
        } finally {
            endSave();
        }
    };

    const handleCellDoubleClick = (ri: number, col: string, currentValue: unknown) => {
        if (!isEditable || editSaving) return;
        const info = colInfoMap[col.toLowerCase()];
        if (!info?.DisplayType) return;
        const dt = info.DisplayType.toUpperCase();
        if (dt === "CHECKBOX" || (!EDITABLE_TEXT_TYPES.has(dt) && dt !== "VALUEDCOMBOBOX")) return;
        setEditCell({ rowIdx: ri, col });
        setEditValue(String(currentValue ?? ""));
    };

    const commitEdit = async () => {
        if (!editCell || !dataSource) return;
        const { rowIdx: ri, col } = editCell;
        const row = dataSource.matrix[ri];
        if (!row) return;

        let finalValue = editValue;
        const info = colInfoMap[col.toLowerCase()];
        const dt = info?.DisplayType?.toUpperCase();

        // TEXTBOXPRICE validation: comma→dot, must be numeric
        if (dt === "TEXTBOXPRICE" && finalValue) {
            finalValue = finalValue.replace(",", ".");
            if (isNaN(parseFloat(finalValue)) || !isFinite(Number(finalValue))) {
                toast("Failed to update cell, needs to be a number", "error");
                setEditCell(null);
                return;
            }
        }

        const idx = colIndex[col] ?? -1;
        const originalValue = String(getCellValue(ri, col, idx >= 0 ? row[idx] : "") ?? "");

        setEditCell(null);

        // Skip the round-trip when the value didn't change AND no override hook
        // wants to inspect the commit. Hooks (e.g. setting-row delete prompt on
        // empty oValue) need to run even on no-op edits — value-equality is not
        // a sufficient signal that nothing should happen.
        if (finalValue === originalValue && !gridOverride?.onCellEditCommit) return;

        await performCellSave(row, col, ri, finalValue, originalValue);
    };

    const commitComboEdit = async (selectedValue: string) => {
        if (!editCell || !dataSource) return;
        const { rowIdx: ri, col } = editCell;
        const row = dataSource.matrix[ri];
        if (!row) return;

        const idx = colIndex[col] ?? -1;
        const originalValue = String(getCellValue(ri, col, idx >= 0 ? row[idx] : "") ?? "");

        setEditCell(null);

        if (selectedValue === originalValue && !gridOverride?.onCellEditCommit) return;

        await performCellSave(row, col, ri, selectedValue, originalValue);
    };

    const handleCheckboxToggle = async (ri: number, col: string, row: unknown[]) => {
        if (!isEditable || editSaving) return;
        const idx = colIndex[col] ?? -1;
        if (idx < 0) return;
        const current = getCellValue(ri, col, row[idx]);
        const newVal = toBoolCheck(current) ? "false" : "true";
        await performCellSave(row, col, ri, newVal, String(current ?? ""));
    };

    const handleConfirmedToggle = async () => {
        if (!confirmAction || !dataSource) return;
        const { rowIdx: ri, col, row, type } = confirmAction;
        setConfirmAction(null);

        const idx = colIndex[col] ?? -1;
        if (idx < 0) return;

        const current = getCellValue(ri, col, row[idx]);
        let newVal: string;
        if (type === "DATELOCK") {
            newVal = new Date().toISOString();
        } else {
            newVal = toBoolCheck(current) ? "false" : "true";
        }

        await performCellSave(row, col, ri, newVal, String(current ?? ""));
    };

    const handleRowClick = (row: unknown[], rowIdx: number, e?: React.MouseEvent) => {
        // Don't open the row modal when the click was an inline edit/action: clicking
        // an editable control (e.g. the "Locked" checkbox, an inline edit input, a
        // combo, or the save/delete buttons) performs that action AND bubbles up to the
        // row — opening the modal on top of an edit is unwanted. A click anywhere else
        // in the row still opens it.
        if (e && (e.target as HTMLElement).closest("input, button, select, textarea")) return;
        const clickedColumn = e ? (e.target as HTMLElement).closest("td[data-col]")?.getAttribute("data-col") ?? undefined : undefined;
        setSelectedRowIdx(rowIdx);
        onRowSelect?.(row, colIndex, rowIdx, clickedColumn);
    };

    const handleSortClick = (header: string) => {
        if (didResizeRef.current) {
            didResizeRef.current = false;
            return;
        }
        if (!hasSearched || editSaving) return;
        // ClientOptions `nosort` disables column sorting entirely.
        if (pageData?.NoSort) return;
        // Don't allow sorting on dynamic columns not in column definitions
        if (!knownColumns.has(header.toLowerCase())) return;
        const existing = sorts.find((s) => s.sColumnName === header);
        if (!existing) {
            setSorts([{ sColumnName: header, OrderType: OrderType.ASC }]);
        } else if (existing.OrderType === OrderType.ASC) {
            setSorts([{ sColumnName: header, OrderType: OrderType.DESC }]);
        } else {
            setSorts(sorts.filter((s) => s.sColumnName !== header));
        }
    };

    const getSortIndicator = (header: string) => {
        const s = sorts.find((x) => x.sColumnName === header);
        if (!s) return null;
        return <span aria-hidden="true">{s.OrderType === OrderType.ASC ? " ▲" : " ▼"}</span>;
    };

    const handleHyperlinkClick = (linkId: number, linkView: string, displayType: string, value: unknown, row: unknown[]) => {
        const action = resolveHyperlinkAction(linkId, linkView, displayType, value, row, colIndex);
        if (action) {
            if (action.kind === "popup") {
                setPopupPath(action.path);
            } else {
                navigate(action.path);
            }
        }
    };

    // Advanced (parameter-only) filter bar — only in classic mode; chips mode
    // opens it in a modal via "+ Filter".
    const showFilters = !compact && filterBarMode === "classic";
    // Inline column-header filter row (FilterBar) — visible in both modes so
    // column-bound filters are editable in place. Chips reflect active state.
    const showInlineColumnFilters = !compact;
    const showToolbar = !compact && !hideToolbar;
    const chipsMode = !compact && filterBarMode === "chips";

    return (
        <FilterSiblingProvider value={siblingStoreRef.current}>
        <div className={styles.grid}>
            {loading && (
                <div className={styles.loadingOverlay}>
                    <div className={styles.loadingSpinner} />
                    <span>Please wait, gathering data</span>
                    {/* Stops the query on the server too, not just the wait here. */}
                    <button type="button" className={styles.toolbarBtn} onClick={cancelSearch}>Cancel</button>
                </div>
            )}
            {error && !loading && (
                <Alert variant="error" title="Couldn't load report" className={styles.gridError}>
                    <div className={styles.gridMessage}>{error}</div>
                </Alert>
            )}
            {cancelled && !loading && (
                <Alert variant="info" title="Search cancelled" className={styles.gridError}>
                    The search was stopped and the database asked to stop working on it. Press Search to run it again.
                </Alert>
            )}
            {/* Stays for as long as the incomplete page is shown; the popup alone can be dismissed. */}
            {incomplete && !loading && (
                <Alert variant="warning" title="Showing incomplete data" className={styles.gridError}>
                    <div className={styles.gridMessage}>
                        <p>{incompleteGridMessage(incomplete)}</p>
                        <p>Reason: {incomplete.message}</p>
                    </div>
                </Alert>
            )}
            {showToolbar && <div className={`${styles.toolbar}${editSaving ? ` ${styles.toolbarDisabled}` : ""}`} aria-disabled={editSaving || undefined}>
                <button className={styles.toolbarBtnPrimary} onClick={searchAndPush} disabled={editSaving}>Search</button>
                {chipsMode && hasAnyFilter && (
                    <button className={styles.toolbarBtn} onClick={() => setShowFilterPanel(true)} title="Edit filters">
                        + Filter
                    </button>
                )}
                {savedFilters.length > 0 && (
                    <DropdownMenu align="left" trigger={
                        <span className={`${styles.toolbarBtn}${savedFilters.some(f => f.isDefault) ? ` ${styles.toolbarBtnActive}` : ""}`}>
                            Views ▾
                        </span>
                    }>
                        {savedFilters.map((f) => (
                            <DropdownMenuItem key={f.id} onClick={() => handleLoadFilter(f)}>
                                {f.isDefault ? "★ " : ""}{f.name}
                            </DropdownMenuItem>
                        ))}
                        <DropdownMenuSeparator />
                        <DropdownMenuItem onClick={() => setShowSavedFilters(true)}>Manage views…</DropdownMenuItem>
                    </DropdownMenu>
                )}
                <div style={{ flex: 1 }} />
                {loading && <span className={styles.loadingText}> Loading...</span>}
                <DropdownMenu trigger={
                    <span className={styles.toolbarBtnMore} title="More" aria-label="More">
                        <StatusIcon kind="moreHorizontal" size={16} />
                    </span>
                }>
                    <DropdownMenuItem onClick={() => { applyColumnSelection(null); clear(); setSelectedRowIdx(null); }}>
                        Clear filters
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => setShowColumnConfig(true)}>
                        {wireColumnList ? `Columns… (${baseVisible.length}/${chooserColumns.length})` : "Columns…"}
                    </DropdownMenuItem>
                    {hasSearched && <>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem onClick={() => setShowScheduleExport(true)}>Schedule export…</DropdownMenuItem>
                        <DropdownMenuItem onClick={() => setShowShareDialog(true)}>Share…</DropdownMenuItem>
                        <DropdownMenuItem onClick={() => {
                            exposeStateInUrl();
                            toast("Current view is now in the URL — bookmark with Ctrl+D", "info");
                        }}>Show in URL</DropdownMenuItem>
                    </>}
                    <DropdownMenuSeparator />
                    <DropdownMenuItem onClick={() => setShowSavedFilters(true)}>Manage views…</DropdownMenuItem>
                </DropdownMenu>
                {toolbarExtra}
            </div>}

            {chipsMode && (filters.length > 0 || paramFilters.length > 0) && (
                <FilterChipStrip
                    filters={filters}
                    setFilters={setFilters}
                    paramFilters={paramFilters}
                    // Bump loadSignal so AdvancedFilterBar's memoized loadedState
                    // rehydrates from the new externalValues — otherwise the child
                    // filter still has the removed value in its internal state.
                    setParamFilters={(next) => { setParamFilters(next); setLoadSignal((s) => s + 1); }}
                    onOpenFilterPanel={() => setShowFilterPanel(true)}
                    displayNames={displayNames}
                    colInfoMap={colInfoMap}
                    commonData={cache?.CommonData?.Common as unknown[] | undefined}
                />
            )}

            {/* Chips mode: mount the filter bar inside a Modal that's always present
                so clearSignal reaches the filter components (otherwise Clear from
                the overflow menu can't re-emit defaults when the panel is closed).
                Modal's open prop toggles visibility; children stay mounted either way. */}
            {chipsMode && cache && (
                <Modal open={showFilterPanel} onClose={() => setShowFilterPanel(false)} wide>
                    <ModalHeader title="Filters" onClose={() => setShowFilterPanel(false)} />
                    <ModalBody>
                        <AdvancedFilterBar
                            params={paramNames}
                            columns={advancedColumns}
                            colInfoMap={colInfoMap}
                            hiddenFiltersSet={hiddenFiltersSet}
                            hiddenColumnsSet={advancedPromoteSet}
                            migratedFilters={pageData?.MigratedFilters}
                            cache={cache}
                            onFiltersChange={editSaving ? () => {} : setParamFilters}
                            onSearch={editSaving ? () => {} : () => { search(); setShowFilterPanel(false); }}
                            clearSignal={clearSignal}
                            loadSignal={loadSignal}
                            externalValues={paramFilters}
                        />
                    </ModalBody>
                    <ModalFooter>
                        <CloseButton onClick={() => setShowFilterPanel(false)} />
                    </ModalFooter>
                </Modal>
            )}

            {showColumnConfig && (
                <Modal open onClose={closeColumnConfig}>
                    <ModalHeader title="Columns" onClose={closeColumnConfig} />
                    <ModalBody>
                        <ColumnConfig
                            columns={chooserColumns}
                            visible={baseVisible}
                            defaultVisible={chooserDefaultVisible}
                            onChange={setVisibleColumns}
                            displayNames={displayNames}
                            tooltips={chooserTooltips}
                            groups={chooserGroups}
                        />
                    </ModalBody>
                    <ModalFooter>
                        <CloseButton onClick={closeColumnConfig} />
                    </ModalFooter>
                </Modal>
            )}

            {showSavedFilters && (
                <Modal open onClose={() => setShowSavedFilters(false)} wide>
                    <ModalHeader title="Saved views" onClose={() => setShowSavedFilters(false)} />
                    <ModalBody>
                        <SavedFiltersPanel
                            gridName={name}
                            currentFilters={filters}
                            currentParamFilters={paramFilters}
                            currentColumns={wireColumnList}
                            savedFilters={savedFilters}
                            displayNames={displayNames}
                            colInfoMap={colInfoMap}
                            onUpdate={setSavedFilters}
                            onLoad={(f) => { handleLoadFilter(f); setShowSavedFilters(false); }}
                        />
                    </ModalBody>
                    <ModalFooter>
                        <CloseButton onClick={() => setShowSavedFilters(false)} />
                    </ModalFooter>
                </Modal>
            )}

            {cache && showFilters && (
                <div className={styles.filterRow}>
                    <AdvancedFilterBar
                        params={paramNames}
                        columns={advancedColumns}
                        colInfoMap={colInfoMap}
                        hiddenFiltersSet={hiddenFiltersSet}
                        hiddenColumnsSet={advancedPromoteSet}
                        migratedFilters={pageData?.MigratedFilters}
                        cache={cache}
                        onFiltersChange={editSaving ? () => {} : setParamFilters}
                        onSearch={editSaving ? () => {} : search}
                        clearSignal={clearSignal}
                        loadSignal={loadSignal}
                        externalValues={paramFilters}
                    />
                    {chartConfig && hasSearched && dataSource && (
                        <button
                            type="button"
                            className={styles.chartExplainBtn}
                            onClick={() => navigate("/page/issainsights", { state: { prompt: buildChartExplainPrompt(dataSource, chartConfig, name) } })}
                            title={`Ask ${assistant.name} to explain this chart`}
                            aria-label={`Ask ${assistant.name} to explain this chart`}
                        >
                            <AssistantAvatar size={14} />
                            Explain
                        </button>
                    )}
                </div>
            )}

            {chartConfig && hasSearched && dataSource && !compact && (
                <DataChart
                    dataSource={dataSource}
                    chartConfig={chartConfig}
                    page={page}
                    pageSize={pageSize}
                    totalCount={knownTotal}
                    onPageChange={setPage}
                    chartTitle={name}
                    hideExplainButton={showFilters}
                    annotations={chartAnnotations}
                    onChartMeta={onChartMeta}
                />
            )}

            {!chartOnly && exportColumns.length === 0 && (
                <EmptyState
                    icon={<StatusIcon kind="search" size={32} />}
                    title="No columns selected"
                    description="All columns are hidden for this grid."
                    action={
                        <>
                            <button type="button" className={styles.toolbarBtnMore} onClick={resetColumnSelection}>
                                Reset to default columns
                            </button>
                            <button type="button" className={styles.toolbarBtnMore} onClick={() => setShowColumnConfig(true)}>
                                Columns…
                            </button>
                        </>
                    }
                />
            )}
            {/* Classic mode has no chip strip, so a column filter that still
                holds a value is only visible if its column happens to be in
                view. This band names every one of them (and scrolls the field
                back into view on click) so a search left in the fields — after
                an endpoint switch, or from an earlier visit — can't go
                unnoticed. */}
            {!chartOnly && showInlineColumnFilters && !chipsMode && (
                <FilterChipStrip
                    variant="notice"
                    label="Active column filters"
                    filters={filters}
                    setFilters={setFilters}
                    paramFilters={[]}
                    setParamFilters={() => {}}
                    onRevealFilter={revealColumnFilter}
                    displayNames={displayNames}
                    colInfoMap={colInfoMap}
                    commonData={cache?.CommonData?.Common as unknown[] | undefined}
                />
            )}

            {!chartOnly && exportColumns.length > 0 && <div className={styles.scrollArea} ref={scrollAreaRef}>
                <table className={styles.table} style={{ minWidth: effectiveVisible.reduce((sum, h) => sum + getColumnWidth(h), 0) }} aria-label={name}>
                    <colgroup>
                        {effectiveVisible.map((h, i) => (
                            <col key={h} style={i < effectiveVisible.length - 1 ? { width: getColumnWidth(h) } : undefined} />
                        ))}
                    </colgroup>
                    <thead className={styles.thead}>
                        <tr>
                            {effectiveVisible.map((h) => {
                                const sortState = sorts.find((s) => s.sColumnName === h);
                                const isSortable = hasSearched && !pageData?.NoSort && knownColumns.has(h.toLowerCase());
                                return (
                                <th
                                    key={h}
                                    onClick={() => handleSortClick(h)}
                                    className={`${styles.th}${isSortable ? ` ${styles.thSortable}` : ""}`}
                                    title={colInfoMap[h.toLowerCase()]?.ToolTip || undefined}
                                    aria-sort={sortState ? (sortState.OrderType === OrderType.ASC ? "ascending" : "descending") : undefined}
                                    tabIndex={isSortable ? 0 : undefined}
                                    onKeyDown={isSortable ? (e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); handleSortClick(h); } } : undefined}
                                >
                                    {renderColumnHeader(h)}
                                    {getSortIndicator(h)}
                                    <span
                                        onMouseDown={(e) => handleResizeStart(e, h)}
                                        className={styles.resizeHandle}
                                        aria-hidden="true"
                                    />
                                </th>
                                );
                            })}
                        </tr>
                        {showInlineColumnFilters && (
                            <FilterBar
                                attributes={{ ...attributes, header: effectiveVisible }}
                                filters={filters}
                                onChange={editSaving ? () => {} : setFilters}
                                onSearch={editSaving ? () => {} : search}
                                displayNames={displayNames}
                                colInfoMap={colInfoMap}
                                hiddenFiltersSet={hiddenFiltersSet}
                                advancedColumnsSet={advancedColumnsSet}
                                migratedFilters={migratedFilters}
                                cache={cache}
                            />
                        )}
                    </thead>
                    <tbody>
                        {hasSearched && (() => {
                            // Compute stripe groups: when RowCountColumn is defined, rows with the same
                            // value share a stripe group; otherwise fall back to row index striping.
                            let stripeGroup = 0;
                            let prevGroupValue: unknown = undefined;
                            return dataSource?.matrix.map((row, rowIdx) => {
                                if (rowCountColIdx >= 0) {
                                    const val = row[rowCountColIdx];
                                    if (rowIdx === 0 || val !== prevGroupValue) {
                                        if (rowIdx > 0) stripeGroup++;
                                        prevGroupValue = val;
                                    }
                                }
                                const stripeIdx = rowCountColIdx >= 0 ? stripeGroup : rowIdx;
                                return (
                            <tr
                                key={rowIdx}
                                onClick={(e) => handleRowClick(row, rowIdx, e)}
                                onContextMenu={(e) => {
                                    // Ctrl+right-click bypasses the custom
                                    // menu so the browser's native context
                                    // menu (inspect, copy text, etc.) shows.
                                    if (e.ctrlKey) return;
                                    // Text the user selected inside this row gets the
                                    // browser's own menu, so its Copy works as anywhere.
                                    const sel = window.getSelection();
                                    if (sel && !sel.isCollapsed && sel.toString().trim() && e.currentTarget.contains(sel.anchorNode)) return;
                                    e.preventDefault();
                                    const cellTexts: Record<string, string> = {};
                                    e.currentTarget.querySelectorAll<HTMLElement>("td[data-col]").forEach((td) => {
                                        cellTexts[td.dataset.col ?? ""] = td.innerText.trim();
                                    });
                                    const column = (e.target as HTMLElement).closest<HTMLElement>("td[data-col]")?.dataset.col;
                                    setRowMenu({ x: e.clientX, y: e.clientY, row, column, cellTexts });
                                }}
                                className={[
                                    onRowSelect ? styles.rowClickable : "",
                                    stripeIdx % (attributes.rowLineCount ?? 2) !== 0 ? styles.rowAlt : "",
                                    selectedRowIdx === rowIdx ? styles.rowSelected : "",
                                    highlightColumn && highlightValue && String(row[colIndex[highlightColumn] ?? -1]) === highlightValue ? styles.rowHighlight : "",
                                ].join(" ")}
                                tabIndex={onRowSelect ? 0 : undefined}
                                onKeyDown={onRowSelect ? (e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); handleRowClick(row, rowIdx); } } : undefined}
                                aria-selected={onRowSelect ? selectedRowIdx === rowIdx : undefined}
                            >
                                {effectiveVisible.map((h) => {
                                    const idx = colIndex[h] ?? -1;
                                    const cellVal = idx >= 0 ? getCellValue(rowIdx, h, row[idx]) : undefined;
                                    const colDt = colInfoMap[h.toLowerCase()]?.DisplayType?.toUpperCase();
                                    const isEditing = editCell?.rowIdx === rowIdx && editCell?.col === h;
                                    const cellState = cellStates[`${rowIdx}:${h}`];
                                    // Row-aware lock predicate runs before cellIsEditable is committed so
                                    // one-shot operations (e.g. Approve payout) freeze the cell once set.
                                    const lockedBuilder = getCellLockedBuilder(gridOverride, h);
                                    const cellLocked = lockedBuilder
                                        ? lockedBuilder({ rowData: row, colIndex, colName: h, rawValue: idx >= 0 ? row[idx] : undefined })
                                        : false;
                                    const cellIsEditable = !cellLocked && isEditable && !!colDt && ALL_EDITABLE_TYPES.has(colDt);
                                    // Single-click for combo + checkbox cells; everything else uses
                                    // double-click. Tooltip mirrors the actual gesture so users with
                                    // the pencil-cue showing know which interaction commits the edit.
                                    const editTooltip = cellIsEditable
                                        ? (colDt === "VALUEDCOMBOBOX" || colDt === "CHECKBOX" || colDt === "CHECKBOXCONFIRM"
                                            ? "Click to edit"
                                            : "Double-click to edit")
                                        : undefined;
                                    return (
                                        <td
                                            key={h}
                                            data-col={h}
                                            className={`${styles.td}${cellState === "saving" ? ` ${styles.cellSaving}` : cellState === "success" ? ` ${styles.cellSuccess}` : cellState === "error" ? ` ${styles.cellError}` : ""}${cellIsEditable ? ` ${styles.cellEditable}` : ""}${isEditable && colDt === "VALUEDCOMBOBOX" ? ` ${styles.comboCell}` : ""}`}
                                            title={editTooltip}
                                            onClick={isEditable && colDt === "VALUEDCOMBOBOX" && idx >= 0 ? (e) => { setEditAnchorEl(e.currentTarget); handleCellDoubleClick(rowIdx, h, cellVal); } : undefined}
                                            onDoubleClick={idx >= 0 ? () => handleCellDoubleClick(rowIdx, h, cellVal) : undefined}
                                        >
                                            {isEditing && colDt === "VALUEDCOMBOBOX" ? (
                                                <ComboBoxEditor
                                                    options={getComboOptions(colInfoMap[h.toLowerCase()])}
                                                    value={editValue}
                                                    onCommit={commitComboEdit}
                                                    onCancel={() => setEditCell(null)}
                                                    anchorEl={editAnchorEl}
                                                />
                                            ) : isEditing && TEXTAREA_TYPES.has(colDt ?? "") ? (
                                                <div className={styles.textareaOverlay}>
                                                    <textarea
                                                        autoFocus
                                                        rows={4}
                                                        value={editValue}
                                                        onChange={e => setEditValue(e.target.value)}
                                                        onBlur={() => commitEdit()}
                                                        onKeyDown={e => {
                                                            if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); commitEdit(); }
                                                            if (e.key === "Escape") setEditCell(null);
                                                        }}
                                                        className={styles.textareaEditor}
                                                    />
                                                </div>
                                            ) : isEditing ? (
                                                <input
                                                    autoFocus
                                                    value={editValue}
                                                    onChange={e => setEditValue(e.target.value)}
                                                    onBlur={() => commitEdit()}
                                                    onKeyDown={e => { if (e.key === "Enter") commitEdit(); if (e.key === "Escape") setEditCell(null); }}
                                                    className={styles.cellEditor}
                                                />
                                            ) : cellIsEditable && colDt === "CHECKBOX" ? (
                                                <input type="checkbox" checked={toBoolCheck(cellVal)} onChange={() => handleCheckboxToggle(rowIdx, h, row)} />
                                            ) : cellIsEditable && CONFIRM_CHECKBOX_TYPES.has(colDt ?? "") ? (
                                                <input
                                                    type="checkbox"
                                                    checked={colDt === "DATELOCK" ? (cellVal != null && String(cellVal) !== "" && String(cellVal) !== "false") : toBoolCheck(cellVal)}
                                                    onChange={() => setConfirmAction({ rowIdx, col: h, row, type: colDt! })}
                                                />
                                            ) : colDt === "COMPONENT_GENERALSAVE" ? (
                                                isEditable ? (
                                                    <button
                                                        type="button"
                                                        className={styles.rowActionBtn}
                                                        title="Save row"
                                                        aria-label="Save row"
                                                        disabled={editSaving}
                                                        onClick={() => handleRowSave(rowIdx, row)}
                                                    >💾</button>
                                                ) : ""
                                            ) : colDt === "COMPONENT_GENERALDELETE" ? (
                                                isEditable ? (
                                                    <button
                                                        type="button"
                                                        className={styles.rowActionBtnDanger}
                                                        title="Delete row"
                                                        aria-label="Delete row"
                                                        disabled={editSaving}
                                                        onClick={() => handleRowDelete(row)}
                                                    >🗑</button>
                                                ) : ""
                                            ) : (
                                                idx >= 0
                                                    ? (() => {
                                                        // Decorator lookup is lifted ABOVE renderCellValue so we can
                                                        // suppress the built-in HYPERLINK onClick when an override is
                                                        // in play. Without this, renderHyperlink wires an onClick that
                                                        // calls e.stopPropagation() and the decorator's wrapping span
                                                        // never sees the click — decorators on HYPERLINK* columns are
                                                        // unable to redirect navigation. Letting the decorator own the
                                                        // click is the right boundary: the decorator gets a plain text
                                                        // node and wires its own handler (typically via deps.openPopup).
                                                        const decorator = getCellDecorator(gridOverride, h);
                                                        const rendered = renderCellValue(
                                                            cellVal,
                                                            colInfoMap[h.toLowerCase()],
                                                            onButtonClick
                                                                ? (info) => onButtonClick(info, row, colIndex, attributes?.updateKeyColumns ?? pageData?.KeyColumns ?? [])
                                                                : undefined,
                                                            {
                                                                rowData: row,
                                                                colIndex,
                                                                commonData: cache?.CommonData?.Common,
                                                                onHyperlinkClick: decorator
                                                                    ? undefined
                                                                    : (linkId, linkView, displayType, value) =>
                                                                        handleHyperlinkClick(linkId, linkView, displayType, value, row),
                                                                ticketStatuses: cache?.TicketStatuses,
                                                                priceTypes: cache?.CommonData?.PriceType,
                                                                csIssues: cache?.CommonData?.CsIssue,
                                                                csIssueTypes: cache?.CommonData?.CsIssueType,
                                                                serverSettings: cache?.ServerSettings,
                                                                gridName: name,
                                                                moduleId: activeEndpoint?.ModuleID,
                                                                colInfoMap,
                                                                onSortColumn: handleSortClick,
                                                                sortIndicator: getSortIndicator,
                                                            },
                                                        );
                                                        const decorated = decorator
                                                            ? decorator(rendered, { rowData: row, colIndex, colName: h, rawValue: cellVal })
                                                            : rendered;
                                                        // Native HTML tooltip surfacing the underlying value (or a
                                                        // LinkView-pointed sibling field). Renderers that paint their own
                                                        // <span title=…> on the inner node still win because the browser
                                                        // picks the innermost element with a title on hover. See
                                                        // resolveCellTooltip for the skip-list and override rules.
                                                        const tooltip = resolveCellTooltip(cellVal, colInfoMap[h.toLowerCase()], row, colIndex);
                                                        return (
                                                            // data-reveal-text opts an editable-but-not-being-edited
                                                            // cell into RevealCharsLayer, so leading/trailing
                                                            // whitespace is banded there too and not only once you
                                                            // click into the editor. See
                                                            // docs/reveal-input-characters.md.
                                                            <div
                                                                className={styles.cellInner}
                                                                title={tooltip}
                                                                data-reveal-text={cellIsEditable ? "" : undefined}
                                                            >
                                                                {decorated}
                                                                {isEditable && colDt === "VALUEDCOMBOBOX" && <span className={styles.comboIndicator}>▾</span>}
                                                            </div>
                                                        );
                                                    })()
                                                    : ""
                                            )}
                                        </td>
                                    );
                                })}
                            </tr>
                                );
                        })})()}
                    </tbody>
                    {hasSearched && dataSource && hasVisibleFooter && !compact && (
                        <tfoot className={styles.tfoot}>
                            <tr>
                                {effectiveVisible.map((h, ci) => {
                                    const fc = footerColumnMap.get(h.toLowerCase());
                                    if (!fc || !fc.showSum) return <td key={h} className={styles.tfootCell}>{ci === 0 && <span className={styles.tfootLabel}>SUM:</span>}</td>;
                                    const raw = dataSource.summary.sum[fc.index];
                                    const val = typeof raw === "number" ? Math.round(raw * 100) / 100 : raw;
                                    return (
                                        <td key={h} className={styles.tfootCell}>
                                            {ci === 0 && <span className={styles.tfootLabel}>SUM:</span>}
                                            {val != null ? String(val) : ""}
                                        </td>
                                    );
                                })}
                            </tr>
                            <tr>
                                {effectiveVisible.map((h, ci) => {
                                    const fc = footerColumnMap.get(h.toLowerCase());
                                    if (!fc || !fc.showAvg) return <td key={h} className={styles.tfootCell}>{ci === 0 && <span className={styles.tfootLabel}>AVG:</span>}</td>;
                                    const raw = dataSource.summary.avg[fc.index];
                                    const val = typeof raw === "number" ? Math.round(raw * 100) / 100 : raw;
                                    return (
                                        <td key={h} className={styles.tfootCell}>
                                            {ci === 0 && <span className={styles.tfootLabel}>AVG:</span>}
                                            {val != null ? String(val) : ""}
                                        </td>
                                    );
                                })}
                            </tr>
                        </tfoot>
                    )}
                </table>
            </div>}

            {!chartOnly && hasSearched && dataSource && !compact && (
                <div className={`${styles.pagerWrap}${editSaving ? ` ${styles.toolbarDisabled}` : ""}`}>
                    <Pagination
                        page={page}
                        pageSize={pageSize}
                        rowCount={dataSource.matrix.length}
                        knownTotal={knownTotal}
                        countLoading={countLoading}
                        countError={countError}
                        uniqueCount={dataSource.uniqueCount}
                        onPageChange={editSaving ? () => {} : setPage}
                        onPageSizeChange={editSaving ? () => {} : setPageSize}
                        onRequestCount={editSaving ? () => {} : fetchCount}
                    />
                    <button
                        className={styles.exportBtn}
                        onClick={() => setShowExport(true)}
                        title="Export to CSV or Excel"
                    >
                        ⬇ Export
                    </button>
                </div>
            )}

            {showExport && (
                <ExportDialog
                    name={name}
                    filters={gridRequestFilters(staticFilters, paramFilters, filters, migratedFilters)}
                    sorts={sorts}
                    columns={exportColumns}
                    isDefaultSelection={isDefaultExportSelection}
                    columnGroups={chooserGroups}
                    onClose={() => setShowExport(false)}
                />
            )}

            {showScheduleExport && (
                <ScheduleExportDialog
                    name={name}
                    filters={filters}
                    paramFilters={paramFilters}
                    sorts={sorts}
                    gridColumns={{
                        columns: chooserColumns,
                        visible: exportColumns,
                        defaultVisible: chooserDefaultVisible,
                        displayNames,
                        tooltips: chooserTooltips,
                        groups: chooserGroups,
                    }}
                    onClose={() => setShowScheduleExport(false)}
                />
            )}

            {showShareDialog && (
                <ShareDialog
                    name={name}
                    filters={[...filters, ...paramFilters]}
                    onClose={() => setShowShareDialog(false)}
                />
            )}

            {confirmAction && (() => {
                const builder = getCellConfirmBuilder(gridOverride, confirmAction.col);
                const colIdx = colIndex[confirmAction.col] ?? -1;
                const rawValue = colIdx >= 0 ? confirmAction.row[colIdx] : undefined;
                const custom = builder?.({
                    rowData: confirmAction.row,
                    colIndex,
                    colName: confirmAction.col,
                    rawValue,
                }) ?? null;
                const title = custom?.title ?? "Confirm?";
                const body = custom?.body ?? <p>Are you really sure you want to do this?</p>;
                const confirmLabel = custom?.confirmLabel ?? "Confirm";
                const cancelLabel = custom?.cancelLabel ?? "Cancel";
                return (
                    <Modal open onClose={() => setConfirmAction(null)}>
                        <ModalHeader title={title} onClose={() => setConfirmAction(null)} />
                        <ModalBody>{body}</ModalBody>
                        <ModalFooter>
                            <button className={styles.toolbarBtn} onClick={() => setConfirmAction(null)}>{cancelLabel}</button>
                            <button className={styles.toolbarBtnPrimary} onClick={handleConfirmedToggle}>{confirmLabel}</button>
                        </ModalFooter>
                    </Modal>
                );
            })()}

            {popupPath && (
                <PagePopup
                    path={popupPath}
                    onClose={(didChange) => {
                        setPopupPath(null);
                        // Skip grid refresh when child explicitly signals
                        // "no change" (e.g. CustomerEventsPage closed without
                        // resolving / editing). Undefined preserves legacy
                        // behaviour: refresh on every close.
                        if (didChange !== false) gridRefresh();
                    }}
                    onNavigate={setPopupPath}
                />
            )}

            {rowMenu && (
                <RowContextMenu
                    x={rowMenu.x}
                    y={rowMenu.y}
                    pageTitle={pageDef?.DisplayName || name}
                    columns={effectiveVisible.map((h) => ({ name: h, title: columnTitle(h) }))}
                    row={rowMenu.row}
                    colIndex={colIndex}
                    column={rowMenu.column}
                    cellTexts={rowMenu.cellTexts}
                    onClose={() => setRowMenu(null)}
                />
            )}
        </div>
        </FilterSiblingProvider>
    );
});

/**
 * Memoised so a host re-render that only flips sibling state (opening a modal,
 * toast, etc.) doesn't re-render the whole grid + every cell. Hosts must pass
 * stable props (useCallback/useMemo) for the skip to actually fire — see
 * GridPage. Default shallow compare; unstable-prop callers just re-render as
 * before (no behaviour change, only a skipped-work opportunity).
 */
export const DataGrid = memo(DataGridImpl);
